import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { X, Users, Car, Clock, ChevronDown, Plus, MapPin, Calendar, AlertCircle, Shield, Phone, Mail, ClipboardList, Package2, DollarSign, CalendarClock, Check } from 'lucide-react';
import { Customer, CustomerVehicle, getCustomer, createCustomerVehicle } from '../lib/customers';
import { getPersonInitials } from '../utils/personInitials';
import { calculateEndAcrossBusinessHours } from '../lib/businessHoursSchedule';
import { registerAssistantSurface } from '../lib/assistant/surface';
import { useBusiness } from '../contexts/BusinessContext';
import { useAppSystem } from '../hooks/useAppSystem';
import { usePermissions } from '../contexts/PermissionContext';
import SchedulePickerPanel from './booking/SchedulePickerPanel';
import { createBooking, linkFieldQuotesToBooking, resolveCandidateFieldQuotes, type FieldQuoteCandidate } from '../lib/bookings';
import { createBookingSeries, SERIES_FREQUENCIES, SERIES_LENGTHS, type SeriesFrequency } from '../lib/bookingSeries';
import { createStockTransaction } from '../lib/stockItems';
import SelectResourceModal from './manage/SelectResourceModal';
import EditVehicleModal from './booking/EditVehicleModal';
import EditCustomerModal from './manage/EditCustomerModal';
import { Service, ServiceAddon, getServiceAddons } from '../lib/services';
import SelectCustomerModal from './manage/SelectCustomerModal';
import ExistingCustomerProposalsModal from './ExistingCustomerProposalsModal';
import {
  getConvertibleProposalsForCustomer,
  getProposal,
  type ConvertibleProposalSummary,
} from '../lib/proposals';
import EditServiceModal from './booking/EditServiceModal';
import CreateVehicleModal from './CreateVehicleModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import SelectServiceModal from './manage/SelectServiceModal';
import SelectInventoryItemModal from './manage/SelectInventoryItemModal';
import RichTextDescription from './RichTextDescription';
import { supabase } from '../lib/supabase';
import SelectServiceAddonsModal from './manage/SelectServiceAddonsModal';
import { ProductPhotoService } from '../services/productPhotoService';
import { executeScheduledTriggers } from '../services/scheduledTriggerProcessor';
import { triggerBookingAutomations } from '../lib/automationsV2';
import { Booking } from '../lib/bookings';
import AddressAutocomplete from './AddressAutocomplete';
import { applyReferralDiscount } from '../lib/referralProgram';
import { applyDiscount } from '../lib/discounts';
import { CustomerVehicle as CustomerVehicleType } from '../types/customers';
// Removed all persistence systems
import ColorPickerModal from './ColorPickerModal';
import { usePowerUps } from '../hooks/usePowerUps';
import CreateColorPresetModal from './CreateColorPresetModal';
import { getColorPresets, createColorPreset, deleteColorPreset, ColorPreset } from '../lib/color-presets';
import { CustomerSourcesService, CustomerSource } from '../lib/customerSources';
import { prefetchSelectCustomers } from '../lib/customers/selectCustomerCache';
import SimpleWorkflowModal from './SimpleWorkflowModal';
import { getResources } from '../lib/resources';
import { useToast } from '../components/ui/use-toast';

// Extend Service interface to include tax_rate and workflow data
interface ExtendedService extends Service {
  tax_rate?: number;
  tax_exempt?: boolean;
  instanceId?: string; // Unique identifier for this service instance
  workflowData?: Array<{
    workflow_step_library_id?: string;
    title: string;
    description?: string;
    order_index: number;
    assigned_resources: Array<{
      id: string;
      name: string;
      avatar_url?: string;
    }>;
  }>;
}

// Service selection with unique instance ID
interface ServiceSelection {
  instanceId: string;
  vehicle: CustomerVehicle | null; // Null for Home Services businesses
  service: ExtendedService;
  priceOption: any;
  // Per-line discount carried from a proposal so it flows into booking_services → invoice
  discount_type?: string;
  discount_value?: number;
  discount_amount?: number;
}

interface ProductSelection {
  instanceId: string;
  vehicle: CustomerVehicle | null; // Null for Home Services businesses
  product: {
    id: string;
    name: string;
    acquisition_cost: number;
    price: number;
    quantity_available: number;
    category_name: string;
    quantity: number; // Quantity selected for this booking
  };
}

interface CreateBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialDate?: Date | null;
  initialProposal?: any; // Proposal data to pre-populate the form
  initialCustomer?: Customer | null; // Customer to pre-populate the form
  initialCustomerSource?: string | null; // Customer source to pre-populate (from project)
  // Warranty-related props
  isWarrantyBooking?: boolean;
  originalBookingId?: string;
  warrantyCustomer?: Customer | null;
  warrantyVehicle?: CustomerVehicle | null;
  // Quick booking props (from calendar time slot)
  initialResource?: { id: string; name: string } | null;
  initialScheduleData?: {
    dropOffDate?: string;
    dropOffTime?: string;
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  } | null;
  // Opportunity link
  initialOpportunityId?: number | null; // Opportunity ID to link the booking to
  // Field quote origin — link the created booking to this field quote
  initialFieldQuoteId?: string | null;
}

interface SavedColor {
  id: string;
  name: string;
  color: string;
  secondary_color?: string;
}

function InlineDiscountForm({ initialDiscount, bookingSubtotal, onApply }: {
  initialDiscount: { title: string; type: 'percentage' | 'fixed'; amount: number } | null;
  bookingSubtotal: number;
  onApply: (discount: { title: string; type: 'percentage' | 'fixed'; amount: number }) => void;
}) {
  const [title, setTitle] = useState(initialDiscount?.title || '');
  const [type, setType] = useState<'percentage' | 'fixed'>(initialDiscount?.type || 'percentage');
  const [amount, setAmount] = useState(initialDiscount?.amount?.toString() || '');

  const discountAmt = type === 'percentage'
    ? Math.min(Math.round((bookingSubtotal * ((parseFloat(amount) || 0) / 100)) * 100) / 100, bookingSubtotal)
    : Math.min(parseFloat(amount) || 0, bookingSubtotal);

  const canApply = title.trim() && parseFloat(amount) > 0;

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Discount Name</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Loyalty discount"
          className="w-full px-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          style={{ fontSize: '16px' }}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Type</label>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button type="button" onClick={() => setType('percentage')}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${type === 'percentage' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            Percentage
          </button>
          <button type="button" onClick={() => setType('fixed')}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${type === 'fixed' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            Fixed Amount
          </button>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">{type === 'percentage' ? 'Percentage' : 'Amount'}</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">{type === 'percentage' ? '%' : '$'}</span>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            min="0"
            max={type === 'percentage' ? '100' : undefined}
            step={type === 'percentage' ? '1' : '0.01'}
            placeholder="0"
            className="w-full pl-8 pr-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            style={{ fontSize: '16px' }}
          />
        </div>
      </div>
      {discountAmt > 0 && (
        <div className="flex items-center justify-between px-3 py-2 bg-green-50 rounded-xl text-sm">
          <span className="text-green-700">Discount</span>
          <span className="font-semibold text-green-700">-${discountAmt.toFixed(2)}</span>
        </div>
      )}
      <button
        type="button"
        onClick={() => onApply({ title: title.trim(), type, amount: parseFloat(amount) || 0 })}
        disabled={!canApply}
        className="w-full py-2.5 text-sm font-semibold text-white rounded-xl transition-all disabled:bg-gray-300 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
      >
        Apply Discount
      </button>
    </div>
  );
}

export default function CreateBookingModal({
  isOpen,
  onClose,
  onSuccess,
  initialDate,
  initialProposal,
  initialCustomer,
  initialCustomerSource,
  isWarrantyBooking = false,
  originalBookingId,
  warrantyCustomer,
  warrantyVehicle,
  initialResource,
  initialScheduleData,
  initialOpportunityId,
  initialFieldQuoteId
}: CreateBookingModalProps) {
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissions();
  const { isPowerUpEnabled } = usePowerUps('services');
  const workflowsEnabled = isPowerUpEnabled('workflows');
  const { toast } = useToast();
  
  // App system hooks
  const { coreApps, installedAddons } = useAppSystem();
  
  // Check if Inventory app is installed
  const isInventoryAppInstalled = coreApps.some(app => app.app?.slug === 'inventory') || 
                                  installedAddons.some(app => app.app?.slug === 'inventory');
  
  // State for inventory settings
  const [showInventoryInBookings, setShowInventoryInBookings] = useState(true);
  
  // Load inventory settings
  useEffect(() => {
    const loadInventorySettings = async () => {
      if (!selectedBusiness?.id || !isInventoryAppInstalled) return;

      try {
        const { data, error } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'inventory',
            p_setting_key: 'show_inventory_in_bookings',
            p_default_value: true
          });

        if (!error && data !== null) {
          setShowInventoryInBookings(data);
        }
      } catch (error) {
        console.error('Error loading inventory settings:', error);
      }
    };

    loadInventorySettings();

    // Listen for setting changes
    const handleInventorySettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_inventory_in_bookings') {
        setShowInventoryInBookings(event.detail.value);
      }
    };

    window.addEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);

    return () => {
      window.removeEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);
    };
  }, [selectedBusiness?.id, isInventoryAppInstalled]);
  
  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('Booking');
  const [isLocationBooking, setIsLocationBooking] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedServices, setSelectedServices] = useState<ServiceSelection[]>([]);
  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [existingProposals, setExistingProposals] = useState<ConvertibleProposalSummary[]>([]);
  const [isExistingProposalsModalOpen, setIsExistingProposalsModalOpen] = useState(false);
  const [convertingExistingProposal, setConvertingExistingProposal] = useState(false);
  const [sourceProposal, setSourceProposal] = useState<any>(null);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [selectedServiceToEdit, setSelectedServiceToEdit] = useState<any>(null);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [selectedVehicleToEdit, setSelectedVehicleToEdit] = useState<CustomerVehicle | null>(null);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isSelectInventoryItemModalOpen, setIsSelectInventoryItemModalOpen] = useState(false);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<ProductSelection[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [businessTaxRates, setBusinessTaxRates] = useState<{ name: string; rate: number }[]>([]);
  const [serviceAdditionalTaxes, setServiceAdditionalTaxes] = useState<{ service_id: string; name: string; rate: number }[]>([]);
  const [scheduleData, setScheduleData] = useState({
    dropOffDate: initialScheduleData?.dropOffDate || '',
    dropOffTime: initialScheduleData?.dropOffTime || '',
    startDate: initialScheduleData?.startDate || '',
    startTime: initialScheduleData?.startTime || '',
    endDate: initialScheduleData?.endDate || '',
    endTime: initialScheduleData?.endTime || '',
    customer_notes: ''
  });

  /** Calendar month → Location booking passes schedule via quickBookingData, but type modal onClose clears selectedTimeSlotData before paint, so props can miss the first useState init; re-apply once when the modal opens. */
  const wasBookingModalOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      wasBookingModalOpenRef.current = false;
      return;
    }
    const justOpened = !wasBookingModalOpenRef.current;
    wasBookingModalOpenRef.current = true;
    if (!justOpened) return;

    const sd = initialScheduleData;
    if (sd && (sd.startDate || sd.startTime || sd.endDate)) {
      setScheduleData({
        dropOffDate: sd.dropOffDate || sd.startDate || '',
        dropOffTime: sd.dropOffTime || sd.startTime || '',
        startDate: sd.startDate || '',
        startTime: sd.startTime || '',
        endDate: sd.endDate || '',
        endTime: sd.endTime || '',
        customer_notes: (sd as { customer_notes?: string }).customer_notes ?? ''
      });
      return;
    }

    if (initialDate) {
      const startDate = format(initialDate, 'yyyy-MM-dd');
      const startTime = format(initialDate, 'HH:mm');
      const endD = new Date(initialDate.getTime());
      endD.setHours(endD.getHours() + 1);
      setScheduleData({
        dropOffDate: startDate,
        dropOffTime: startTime,
        startDate,
        startTime,
        endDate: format(endD, 'yyyy-MM-dd'),
        endTime: format(endD, 'HH:mm'),
        customer_notes: ''
      });
    }
  }, [isOpen, initialScheduleData, initialDate]);
  const [bookingColor, setBookingColor] = useState('#3b82f6'); // Default blue
  const [bookingSecondaryColor, setBookingSecondaryColor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Field-quote link confirmation gate (shown before the booking is actually created)
  const [fqPickerOpen, setFqPickerOpen] = useState(false);
  const [fqCandidates, setFqCandidates] = useState<FieldQuoteCandidate[]>([]);
  const [fqSelectedIds, setFqSelectedIds] = useState<string[]>([]);
  // null = not resolved yet (prompt if needed); array = already decided this submit
  const resolvedFieldQuoteIdsRef = useRef<string[] | null>(null);

  // Reset the field-quote gate each time the modal opens so a fresh booking re-prompts.
  useEffect(() => {
    if (isOpen) {
      resolvedFieldQuoteIdsRef.current = null;
      setFqPickerOpen(false);
      setFqCandidates([]);
      setFqSelectedIds([]);
    }
  }, [isOpen]);
  const [isSelectAddonsModalOpen, setIsSelectAddonsModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState<ExtendedService | null>(null);
  const [selectedServiceAddons, setSelectedServiceAddons] = useState<Record<string, ServiceAddon[]>>({});
  const [isColorPickerModalOpen, setIsColorPickerModalOpen] = useState(false);
  const [isCreateColorPresetModalOpen, setIsCreateColorPresetModalOpen] = useState(false);
  const [colorPresets, setColorPresets] = useState<ColorPreset[]>([]);
  const [selectedColorMethod, setSelectedColorMethod] = useState<'custom' | string>('custom'); // 'custom' or preset id
  
  // Customer Source state
  const [customerSources, setCustomerSources] = useState<CustomerSource[]>([]);
  const [selectedCustomerSource, setSelectedCustomerSource] = useState<CustomerSource | null>(null);
  const [isCustomerSourceDropdownOpen, setIsCustomerSourceDropdownOpen] = useState(false);
  const [isCreateSourceModalOpen, setIsCreateSourceModalOpen] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [createSourceLoading, setCreateSourceLoading] = useState(false);
  
  // Referral program state
  const [referralProgramActive, setReferralProgramActive] = useState(false);
  const [referralSourceCustomer, setReferralSourceCustomer] = useState<Customer | null>(null);
  const [isSelectReferralSourceModalOpen, setIsSelectReferralSourceModalOpen] = useState(false);
  
  // Simple workflow state
  const [isSimpleWorkflowModalOpen, setIsSimpleWorkflowModalOpen] = useState(false);
  const [currentServiceForWorkflow, setCurrentServiceForWorkflow] = useState<ExtendedService | null>(null);
  
  // Customer notes expansion state
  const [isCustomerNotesExpanded, setIsCustomerNotesExpanded] = useState(false);
  
  // Resources expansion state
  const [isResourcesExpanded, setIsResourcesExpanded] = useState(false);
  
  // Schedule picker state
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  // Unscheduled ("no date yet") mode — creates the booking in a limbo state.
  const [isUnscheduled, setIsUnscheduled] = useState(false);
  const [removeFromAutomations, setRemoveFromAutomations] = useState(false);
  // Assets picked from the week-view schedule grid (assets share the grid with
  // resources). Passed to createBooking as asset_ids → booking_assets.
  const [selectedBookingAssets, setSelectedBookingAssets] = useState<Array<{ id: string; name: string; image_url?: string | null }>>([]);
  // Recurring booking series — fans out repeat bookings after the first is created.
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<SeriesFrequency>('monthly');
  const [recurringLengthMonths, setRecurringLengthMonths] = useState(12);
  
  // Collapsible section states
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [isCustomerSectionExpanded, setIsCustomerSectionExpanded] = useState(true);
  const [isServicesSectionExpanded, setIsServicesSectionExpanded] = useState(true);
  const [expandedServiceDescriptions, setExpandedServiceDescriptions] = useState<Set<string>>(new Set());
  const [serviceHasAddons, setServiceHasAddons] = useState<Record<string, boolean>>({});
  const [pendingDiscount, setPendingDiscount] = useState<{ title: string; type: 'percentage' | 'fixed'; amount: number } | null>(null);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);

  // Auto-collapse customer section when services are added
  useEffect(() => {
    if (selectedServices.length > 0 && selectedCustomer) {
      setIsCustomerSectionExpanded(false);
    }
  }, [selectedServices.length]);

  // Precompute which services have add-ons available
  useEffect(() => {
    const serviceIds = [...new Set(selectedServices.map(s => s.service.id))];
    serviceIds.forEach(async (id) => {
      if (serviceHasAddons[id] !== undefined) return;
      try {
        const addons = await getServiceAddons(id);
        setServiceHasAddons(prev => ({ ...prev, [id]: !!(addons && addons.length > 0) }));
      } catch { /* ignore */ }
    });
  }, [selectedServices.map(s => s.service.id).join(',')]);
  
  // Track which services have workflow templates and their assignment status
  const [servicesWithWorkflows, setServicesWithWorkflows] = useState<Set<string>>(new Set());
  const [servicesWithAssignedWorkflows, setServicesWithAssignedWorkflows] = useState<Set<string>>(new Set());
  
  // Proposal deposit tracking
  const [proposalDepositPaid, setProposalDepositPaid] = useState<number>(0);
  
  // Ref for customer source dropdown positioning
  const customerSourceButtonRef = useRef<HTMLButtonElement>(null);
  
  // Warranty booking state
  const [isWarrantyMode, setIsWarrantyMode] = useState(isWarrantyBooking);
  const [originalBookingNumber, setOriginalBookingNumber] = useState<string>('Unknown');
  
  useEffect(() => {
    if (!selectedBusiness?.id) return;
    supabase
      .from('tax_rates')
      .select('name, rate')
      .eq('business_id', selectedBusiness.id)
      .then(({ data }) => { if (data) setBusinessTaxRates(data); });
  }, [selectedBusiness?.id]);

  useEffect(() => {
    const serviceIds = selectedServices.map(s => s.service.id);
    if (serviceIds.length === 0) { setServiceAdditionalTaxes([]); return; }
    supabase
      .from('service_additional_taxes')
      .select('service_id, name, rate')
      .in('service_id', serviceIds)
      .then(({ data }) => { if (data) setServiceAdditionalTaxes(data); });
  }, [selectedServices]);

  const combinedBusinessRate = businessTaxRates.reduce((sum, r) => sum + r.rate, 0) / 100;
  const businessTaxLabel = businessTaxRates.length > 0
    ? businessTaxRates.map(r => `${r.name} (${r.rate}%)`).join(' + ')
    : 'Tax';

  // Helper function to save current form state
  const saveBookingProgress = (overrides = {}) => {
    const formData = {
      title,
      isLocationBooking,
      selectedCustomer,
      currentVehicle,
      selectedServices,
      // scheduleData deliberately excluded - we don't want to persist schedule across sessions
      bookingColor,
      bookingSecondaryColor,
      selectedServiceAddons,
      selectedResources,
      ...overrides // Allow overriding specific values for immediate saves
    };
    console.log('💾 Saving progress with data:', Object.keys(formData), formData.selectedCustomer ? `Customer: ${formData.selectedCustomer.first_name} ${formData.selectedCustomer.last_name}` : 'No customer');
    // Note: Persistence now handled centrally by usePageLifecycle
  };

  // Function to load proposal data into the form
  const loadProposalData = async (proposalSource?: any) => {
    const proposal = proposalSource ?? initialProposal;
    if (!proposal) return;
    
    try {
      console.log('Loading proposal data:', proposal);
      
      // Set the title from the proposal
      setTitle(proposal.title || 'Booking');
      
      // Load customer data and their vehicles
      let customerVehicles: CustomerVehicle[] = [];
      if (proposal.customer_id) {
        const customerData = await getCustomer(proposal.customer_id);
        setSelectedCustomer(customerData);
        // Auto-detect customer source based on repeat status
        if (!initialCustomerSource) {
          detectCustomerSource(customerData);
        }
        
        // Get all vehicles for this customer (getCustomer already includes vehicles, but keeping this for compatibility)
        if (supabase) {
          const { data: vehicles, error: vehicleError } = await supabase
            .from('customer_vehicles')
            .select('*')
            .eq('customer_id', proposal.customer_id);
          
          if (!vehicleError && vehicles) {
            customerVehicles = vehicles;
            console.log('Loaded customer vehicles:', customerVehicles);
          }
        }
        
        // Use vehicles from customerData if available (getCustomer already includes them)
        if (customerData.vehicles && customerData.vehicles.length > 0) {
          customerVehicles = customerData.vehicles;
        }
      }
      
      // Prefer approved offer; otherwise use the first offer (open sent/viewed proposals)
      const approvedOffer = proposal.offers?.find((o: any) => o.status === 'approved')
        || [...(proposal.offers || [])].sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0))[0];
      console.log('Approved offer:', approvedOffer);
      
      if (approvedOffer && approvedOffer.services) {
        // Filter for selected services on approved offers; take all lines for open proposals
        const selectedProposalServices = approvedOffer.services.filter((service: any) => {
          if (proposal.status !== 'approved') return true;
          const isSelectable = service.isSelectable || service.is_selectable;
          const isSelected = service.is_selected;
          
          // Include if not selectable OR (selectable AND selected)
          return !isSelectable || (isSelectable && isSelected);
        });
        
        console.log('Selected proposal services:', selectedProposalServices);
        
        // Convert proposal services to booking services format
        const bookingServices = [];
        for (const proposalService of selectedProposalServices) {
          // Find the vehicle from the customer's vehicles, or fetch directly
          let vehicleData = customerVehicles.find(v => v.id === proposalService.vehicle_id);
          
          // If not found in the local list but we have a vehicle_id, fetch directly from DB
          if (!vehicleData && proposalService.vehicle_id && supabase) {
            const { data: directVehicle } = await supabase
              .from('customer_vehicles')
              .select('*')
              .eq('id', proposalService.vehicle_id)
              .single();
            if (directVehicle) {
              vehicleData = directVehicle;
              customerVehicles.push(directVehicle);
            }
          }
          
          console.log(`Looking for vehicle ${proposalService.vehicle_id}, found:`, vehicleData);
          
          if (vehicleData && supabase) {
            // Get the actual service data from the database
            const { data: serviceData, error: serviceError } = await supabase
              .from('services')
              .select('*')
              .eq('id', proposalService.service_id)
              .single();
            
            if (!serviceError && serviceData) {
              const name = proposalService.name != null ? proposalService.name : serviceData.name;
              const description = proposalService.description != null ? proposalService.description : (serviceData.description ?? '');
              const price = proposalService.price != null ? proposalService.price : (serviceData.price_options?.[0]?.price ?? serviceData.price ?? 0);
              const durationMin = proposalService.duration_minutes != null ? proposalService.duration_minutes : (serviceData.price_options?.[0]?.duration_minutes ?? serviceData.duration_minutes ?? 60);
              const pricingType = proposalService.pricing_type || 'flat';
              const pUnitPrice = proposalService.unit_price ?? null;
              const pDurationPerUnit = proposalService.duration_per_unit ?? null;
              const pQuantity = proposalService.quantity ?? 0;

              const priceOptionObj = {
                id: proposalService.service_id,
                service_id: proposalService.service_id,
                name: 'Standard',
                price,
                duration_minutes: durationMin,
                pricing_type: pricingType,
                unit_price: pUnitPrice,
                duration_per_unit: pDurationPerUnit,
                quantity: pQuantity
              };
              const service: ExtendedService = {
                ...serviceData,
                name,
                description: description ?? '',
                tax_rate: proposalService.tax_rate,
                tax_exempt: proposalService.tax_exempt,
                price_options: [priceOptionObj]
              };
              
              bookingServices.push({
                instanceId: `${service.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                vehicle: vehicleData,
                service,
                priceOption: priceOptionObj,
                discount_type: proposalService.discount_type === 'percentage' ? 'percentage' : 'fixed',
                discount_value: proposalService.discount_value || 0,
                discount_amount: proposalService.discount_amount || 0
              });
            }
          } else {
            const vehicleId = proposalService.vehicle_id || 'no-vehicle-placeholder';
            const minimalVehicle: CustomerVehicle = {
              id: vehicleId,
              customer_id: proposal.customer_id,
              make: proposalService.vehicle_make || 'Unknown',
              model: proposalService.vehicle_model || 'Vehicle',
              year: proposalService.vehicle_year || new Date().getFullYear(),
              color: proposalService.vehicle_color || '',
              license_plate: proposalService.vehicle_license_plate || '',
              vin: proposalService.vehicle_vin || '',
              mileage: 0,
              notes: '',
              is_primary: proposalService.vehicle_is_primary || false,
              last_service: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            if (supabase) {
              const { data: serviceData, error: serviceError } = await supabase
                .from('services')
                .select('*')
                .eq('id', proposalService.service_id)
                .single();
              
              if (!serviceError && serviceData) {
                const name = proposalService.name != null ? proposalService.name : serviceData.name;
                const description = proposalService.description != null ? proposalService.description : (serviceData.description ?? '');
                const price = proposalService.price != null ? proposalService.price : (serviceData.price_options?.[0]?.price ?? serviceData.price ?? 0);
                const durationMin = proposalService.duration_minutes != null ? proposalService.duration_minutes : (serviceData.price_options?.[0]?.duration_minutes ?? serviceData.duration_minutes ?? 60);
                const pricingType2 = proposalService.pricing_type || 'flat';
                const pUnitPrice2 = proposalService.unit_price ?? null;
                const pDurationPerUnit2 = proposalService.duration_per_unit ?? null;
                const pQuantity2 = proposalService.quantity ?? 0;

                const priceOptionObj2 = {
                  id: proposalService.service_id,
                  service_id: proposalService.service_id,
                  name: 'Standard',
                  price,
                  duration_minutes: durationMin,
                  pricing_type: pricingType2,
                  unit_price: pUnitPrice2,
                  duration_per_unit: pDurationPerUnit2,
                  quantity: pQuantity2
                };
                const service: ExtendedService = {
                  ...serviceData,
                  name,
                  description: description ?? '',
                  tax_rate: proposalService.tax_rate,
                  tax_exempt: proposalService.tax_exempt,
                  price_options: [priceOptionObj2]
                };
                
                bookingServices.push({
                  instanceId: `${service.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  vehicle: minimalVehicle,
                  service,
                  priceOption: priceOptionObj2,
                  discount_type: proposalService.discount_type === 'percentage' ? 'percentage' : 'fixed',
                  discount_value: proposalService.discount_value || 0,
                  discount_amount: proposalService.discount_amount || 0
                });
              }
            }
          }
        }
        
        console.log('Final booking services:', bookingServices);
        setSelectedServices(bookingServices);
        
        // Set the first vehicle as current if available
        if (bookingServices.length > 0) {
          setCurrentVehicle(bookingServices[0].vehicle);
        }
      }

      // Handle products from the approved offer
      if (approvedOffer && approvedOffer.products) {
        console.log('Loading proposal products:', approvedOffer.products);
        
        // Convert proposal products to booking products format
        const bookingProducts = [];
        for (const proposalProduct of approvedOffer.products) {
          // Find the vehicle from the customer's vehicles
          const vehicleData = customerVehicles.find(v => v.id === proposalProduct.vehicle_id);
          
          console.log(`Looking for vehicle ${proposalProduct.vehicle_id} for product ${proposalProduct.name}, found:`, vehicleData);
          
          if (vehicleData && supabase) {
            // Get the actual stock item data from the database
            const { data: stockItemData, error: stockError } = await supabase
              .from('stock_items')
              .select('*')
              .eq('id', proposalProduct.stock_item_id)
              .single();
            
            if (!stockError && stockItemData) {
              // Create product object matching the expected format
              const product = {
                id: stockItemData.id,
                name: stockItemData.name,
                price: proposalProduct.price, // Use proposal price, not stock price
                quantity: proposalProduct.quantity,
                category_name: proposalProduct.category_name,
                acquisition_cost: stockItemData.acquisition_cost,
                quantity_available: stockItemData.quantity
              };
              
              bookingProducts.push({
                instanceId: `${product.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                vehicle: vehicleData,
                product
              });
            }
          } else if (proposalProduct.vehicle_id) {
            // If we can't find the vehicle, create a minimal vehicle object
            // Use a placeholder ID if vehicle_id is null to avoid string "null" issues
            const vehicleId = proposalProduct.vehicle_id || 'no-vehicle-placeholder';
            const minimalVehicle: CustomerVehicle = {
              id: vehicleId,
              customer_id: proposal.customer_id,
              make: 'Unknown',
              model: 'Vehicle',
              year: 2020, // Default year
              color: null,
              license_plate: null,
              vin: null,
              mileage: null,
              notes: null,
              is_primary: false,
              last_service: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            // Create product object with available data
            const product = {
              id: proposalProduct.stock_item_id,
              name: proposalProduct.name,
              price: proposalProduct.price,
              quantity: proposalProduct.quantity,
              category_name: proposalProduct.category_name,
              acquisition_cost: 0, // Default since we don't have stock data
              quantity_available: proposalProduct.quantity // Assume available
            };
            
            bookingProducts.push({
              instanceId: `${product.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              vehicle: minimalVehicle,
              product
            });
          }
        }
        
        console.log('Final booking products:', bookingProducts);
        setSelectedProducts(bookingProducts);
        
        // Load product photos for the selected products
        if (bookingProducts.length > 0) {
          try {
            const stockItemIds = bookingProducts.map(p => p.product.id);
            const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
            
            // Convert to the format expected by the component
            const photoUrls: Record<string, string> = {};
            if (photoMap && typeof photoMap === 'object') {
              Object.entries(photoMap).forEach(([itemId, photo]) => {
                if (photo && typeof photo === 'object' && 'photo_url' in photo) {
                  photoUrls[itemId] = (photo as any).photo_url;
                }
              });
            }
            setProductPhotos(photoUrls);
          } catch (photoError) {
            console.error('Error loading product photos:', photoError);
            setProductPhotos({});
          }
        }
      }
    } catch (error) {
      console.error('Error loading proposal data:', error);
    }
  };

  // Load saved data when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📥 Loading CreateBookingModal progress...');
      // Note: Persistence now handled centrally by usePageLifecycle
      console.log('📥 Progress loading handled by centralized system');
      
      // If we have a proposal, load its data
      if (initialProposal) {
        loadProposalData();
        loadProposalDeposits();
      }
    }
  }, [isOpen, initialProposal]);
  
  // Separate useEffect for initial resource (only set once when modal opens with initial data)
  useEffect(() => {
    if (isOpen && initialResource) {
      console.log('👤 Setting initial resource:', initialResource);
      // Only set if selectedResources is empty (avoid overwriting user selections)
      setSelectedResources(prev => prev.length === 0 ? [initialResource] : prev);
    }
  }, [isOpen]);

  // Function to load proposal or project deposits
  const loadProposalDeposits = async (proposalSource?: any) => {
    const proposal = proposalSource ?? sourceProposal ?? initialProposal;
    if (!proposal?.id || !supabase) return;

    const isProject = (proposal as any)?.isProject;
    
    try {
      // Load payments from either proposal or project
      const { data: payments, error } = await supabase
        .from('payments')
        .select('*')
        .eq(isProject ? 'project_id' : 'proposal_id', proposal.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error(`Error fetching ${isProject ? 'project' : 'proposal'} payments:`, error);
        return;
      }

      // Calculate deposit paid from completed deposit payments
      const completedDepositPayments = (payments || []).filter(
        p => p.status === 'completed' && p.payment_type === 'deposit'
      );
      const totalDepositPaid = completedDepositPayments.reduce(
        (sum, payment) => sum + (payment.amount || 0), 
        0
      );
      
      console.log(`💰 Loaded ${isProject ? 'project' : 'proposal'} deposits:`, {
        totalPayments: payments?.length || 0,
        depositPayments: completedDepositPayments.length,
        totalDepositPaid
      });
      
      setProposalDepositPaid(totalDepositPaid);
    } catch (error) {
      console.error(`Error loading ${isProject ? 'project' : 'proposal'} deposits:`, error);
    }
  };

  // Generate time options in 30 minute increments
  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return {
      value: `${hour.toString().padStart(2, '0')}:${minute}`,
      label: `${displayHour}:${minute} ${ampm}`
    };
  });

  // Calculate total duration in minutes
  const totalDurationMinutes = selectedServices.reduce((total, service) => {
    // Add service duration
    let duration = total + service.priceOption.duration_minutes;
    // Add addon durations for this service
    const addons = selectedServiceAddons[service.service.id] || [];
    duration += addons.reduce((addonSum, addon) => addonSum + addon.duration_minutes, 0);
    return duration;
  }, 0);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      // Reset state when modal closes to prevent state persistence issues
      setTitle('Booking');
      setSelectedCustomer(null);
      setSelectedServices([]);
      setSelectedProducts([]); // Clear products to prevent cross-booking contamination
      setCurrentVehicle(null);
      setSourceProposal(null);
      setExistingProposals([]);
      setIsExistingProposalsModalOpen(false);
      setConvertingExistingProposal(false);
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setBookingColor('#3b82f6');
      setBookingSecondaryColor(null);
      setSelectedResources([]);
      setSelectedServiceAddons({});
      setProductPhotos({});
      setError(null);
      setSelectedCustomerSource(null);
      setIsTitleExpanded(false);
      setIsCustomerSectionExpanded(true);
      setIsServicesSectionExpanded(true);
      setIsResourcesExpanded(false);
      setSchedulePickerOpen(false);
      setIsUnscheduled(false);
      setExpandedServiceDescriptions(new Set());
      setServiceHasAddons({});
      setPendingDiscount(null);
      setIsDiscountModalOpen(false);
      
      console.log('🧹 CreateBookingModal state reset - cleared selectedProducts and other state');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedBusiness?.id) {
      // Load color presets from database
      loadColorPresets();
    }
  }, [selectedBusiness?.id]);

  const loadColorPresets = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const presets = await getColorPresets(selectedBusiness.id);
      setColorPresets(presets);
    } catch (error) {
      console.error('Error loading color presets:', error);
    }
  };

  // Load customer sources when modal opens
  useEffect(() => {
    console.log('🔄 [CreateBookingModal] useEffect for loadCustomerSources triggered:', {
      isOpen,
      selectedBusinessId: selectedBusiness?.id,
      willLoadSources: isOpen && selectedBusiness?.id
    });
    
    if (isOpen && selectedBusiness?.id) {
      loadCustomerSources();
      loadReferralProgramStatus();
      // Warm Select Customer so opening it feels instant.
      void prefetchSelectCustomers(selectedBusiness.id);
    }
  }, [isOpen, selectedBusiness?.id]);

  // Set initial customer source when provided (from project conversion)
  useEffect(() => {
    if (isOpen && initialCustomerSource && customerSources.length > 0) {
      // Find the matching source in our customer sources list
      const matchingSource = customerSources.find(
        source => source.name.toLowerCase() === initialCustomerSource.toLowerCase()
      );
      
      if (matchingSource) {
        console.log('📋 [CreateBookingModal] Setting initial customer source from project:', matchingSource.name);
        setSelectedCustomerSource(matchingSource);
      } else {
        // Create a virtual source object for display if not found
        console.log('📋 [CreateBookingModal] Creating virtual source from project:', initialCustomerSource);
        setSelectedCustomerSource({
          id: 'project-source',
          business_id: selectedBusiness?.id || '',
          name: initialCustomerSource,
          is_active: true,
          created_at: '',
          updated_at: ''
        });
      }
    }
  }, [isOpen, initialCustomerSource, customerSources, selectedBusiness?.id]);

  // Initialize warranty booking data when modal opens
  useEffect(() => {
    if (isOpen && isWarrantyBooking) {
      // Try to get warranty data from localStorage first
      const storedWarrantyData = localStorage.getItem('warrantyBookingData');
      if (storedWarrantyData) {
        try {
          const warrantyData = JSON.parse(storedWarrantyData);
          const now = Date.now();
          const dataAge = now - warrantyData.timestamp;
          
          // Only use data that's less than 5 minutes old
          if (dataAge < 5 * 60 * 1000 && warrantyData.customer && warrantyData.vehicle) {
            console.log('🛡️ [CreateBookingModal] Loading warranty data from localStorage:', {
              customer: warrantyData.customer.first_name + ' ' + warrantyData.customer.last_name,
              vehicle: `${warrantyData.vehicle.year} ${warrantyData.vehicle.make} ${warrantyData.vehicle.model}`,
              originalBookingId: warrantyData.originalBookingId
            });
            
            // Pre-populate customer and vehicle
            setSelectedCustomer(warrantyData.customer);
            setCurrentVehicle(warrantyData.vehicle);
            
            // Set title to indicate this is a warranty booking
            setTitle('Warranty Booking');
            
                         // Set warranty mode
             setIsWarrantyMode(true);
             
             // Set the original booking number
             setOriginalBookingNumber(warrantyData.originalBookingNumber || 'Unknown');
             
             // Set warranty customer source
             setSelectedCustomerSource({ 
               id: 'warranty', 
               business_id: selectedBusiness?.id || '',
               name: 'Warranty', 
               is_active: true,
               created_at: '',
               updated_at: ''
             });
             
             // Get original booking ID from localStorage (set by App component)
             const storedOriginalBookingId = localStorage.getItem('warrantyOriginalBookingId');
             if (storedOriginalBookingId) {
               console.log('🛡️ [CreateBookingModal] Found original booking ID in localStorage:', storedOriginalBookingId);
               // Store it in the warranty data for later use
               warrantyData.originalBookingId = storedOriginalBookingId;
             }
             
             // Don't clear localStorage yet - we need it for booking creation
             // localStorage.removeItem('warrantyBookingData');
             // localStorage.removeItem('warrantyOriginalBookingId');
             return;
          } else {
            // Data is too old, remove it
            localStorage.removeItem('warrantyBookingData');
          }
        } catch (error) {
          console.error('Error parsing warranty data from localStorage:', error);
          localStorage.removeItem('warrantyBookingData');
        }
      }
      
      // Fallback to props if available
      if (warrantyCustomer && warrantyVehicle) {
        console.log('🛡️ [CreateBookingModal] Initializing warranty booking with props:', {
          customer: warrantyCustomer.first_name + ' ' + warrantyCustomer.last_name,
          vehicle: `${warrantyVehicle.year} ${warrantyVehicle.make} ${warrantyVehicle.model}`,
          originalBookingId
        });
        
        // Pre-populate customer and vehicle
        setSelectedCustomer(warrantyCustomer);
        setCurrentVehicle(warrantyVehicle);
        
        // Set title to indicate this is a warranty booking
        setTitle('Warranty Booking');
        
        // Set warranty mode
        setIsWarrantyMode(true);
        
                 // Set the original booking number from props if available
         if (originalBookingId) {
           setOriginalBookingNumber(originalBookingId.slice(-8));
         }
         
         // Set warranty customer source
         setSelectedCustomerSource({ 
           id: 'warranty', 
           business_id: selectedBusiness?.id || '',
           name: 'Warranty', 
           is_active: true,
           created_at: '',
           updated_at: ''
         });
         
         // Get original booking ID from localStorage (set by App component)
         const storedOriginalBookingId = localStorage.getItem('warrantyOriginalBookingId');
         if (storedOriginalBookingId) {
           console.log('🛡️ [CreateBookingModal] Found original booking ID in localStorage (fallback):', storedOriginalBookingId);
           // Update the originalBookingId prop for later use
           originalBookingId = storedOriginalBookingId;
         }
      }
    }
  }, [isOpen, isWarrantyBooking, warrantyCustomer, warrantyVehicle, originalBookingId]);

  // Initialize customer when modal opens (for non-warranty bookings)
  useEffect(() => {
    const loadInitialCustomer = async () => {
      if (isOpen && !isWarrantyBooking && initialCustomer) {
        console.log('📅 [CreateBookingModal] Pre-populating customer:', {
          customer: initialCustomer.first_name + ' ' + initialCustomer.last_name
        });
        // Fetch full customer with vehicles if customer ID is available
        if (initialCustomer.id) {
          try {
            const fullCustomer = await getCustomer(initialCustomer.id);
            setSelectedCustomer(fullCustomer);
            // Auto-detect customer source based on repeat status
            if (!initialCustomerSource) {
              detectCustomerSource(fullCustomer);
            }
          } catch (error) {
            console.error('Error loading customer with vehicles:', error);
            setSelectedCustomer(initialCustomer);
            // Auto-detect customer source based on repeat status
            if (!initialCustomerSource) {
              detectCustomerSource(initialCustomer);
            }
          }
        } else {
          setSelectedCustomer(initialCustomer);
          // Auto-detect customer source based on repeat status
          if (!initialCustomerSource) {
            detectCustomerSource(initialCustomer);
          }
        }
      }
    };
    
    loadInitialCustomer();
  }, [isOpen, isWarrantyBooking, initialCustomer]);

  // AI assistant: set the customer source directly (assistant:setCustomerSource).
  // The source object comes from the assistant's set_customer_source tool, which
  // matched or created it in customer_sources beforehand.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      const source = (e as CustomEvent).detail?.source as CustomerSource | undefined;
      if (!source?.name) return;
      setCustomerSources((prev) =>
        prev.some((s) => s.id === source.id)
          ? prev
          : [...prev, source].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setSelectedCustomerSource(source);
      setIsCustomerSourceDropdownOpen(false);
      if (source.name.toLowerCase() !== 'referral') setReferralSourceCustomer(null);
    };
    window.addEventListener('assistant:setCustomerSource', handler);
    return () => window.removeEventListener('assistant:setCustomerSource', handler);
  }, [isOpen]);

  const loadCustomerSources = async () => {
    console.log('🔍 [CreateBookingModal] loadCustomerSources called:', {
      selectedBusinessId: selectedBusiness?.id,
      selectedBusinessName: selectedBusiness?.name,
      hasSupabase: !!supabase
    });
    
    if (!selectedBusiness?.id) {
      console.warn('❌ [CreateBookingModal] No selectedBusiness.id available');
      return;
    }
    
    try {
      console.log('📡 [CreateBookingModal] Calling CustomerSourcesService.getCustomerSources...');
      const sources = await CustomerSourcesService.getCustomerSources(selectedBusiness.id);
      console.log('✅ [CreateBookingModal] Customer sources loaded:', {
        count: sources.length,
        sources: sources.map(s => ({ id: s.id, name: s.name, is_active: s.is_active }))
      });
      
      // Default sources that should always be available
      const defaultSources: CustomerSource[] = [
        {
          id: 'repeat-customer',
          business_id: selectedBusiness.id,
          name: 'Repeat Customer',
          is_active: true,
          created_at: '',
          updated_at: ''
        },
        {
          id: 'referral',
          business_id: selectedBusiness.id,
          name: 'Referral',
          is_active: true,
          created_at: '',
          updated_at: ''
        }
      ];
      
      // Merge default sources with database sources, avoiding duplicates
      const allSources = [...defaultSources];
      sources.forEach(source => {
        // Don't add if a default source with the same name exists
        if (!defaultSources.some(ds => ds.name.toLowerCase() === source.name.toLowerCase())) {
          allSources.push(source);
        }
      });
      
      // Sort alphabetically by name
      allSources.sort((a, b) => a.name.localeCompare(b.name));
      
      setCustomerSources(allSources);
    } catch (error) {
      console.error('❌ [CreateBookingModal] Error loading customer sources:', {
        error: error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        businessId: selectedBusiness.id,
        stack: error instanceof Error ? error.stack : undefined
      });
    }
  };

  // Load referral program status
  const loadReferralProgramStatus = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('referral_program')
        .select('is_active')
        .eq('business_id', selectedBusiness.id)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading referral program status:', error);
        setReferralProgramActive(false);
        return;
      }
      
      const isActive = data?.is_active || false;
      console.log('📊 Referral program status loaded:', { isActive, data });
      setReferralProgramActive(isActive);
    } catch (error) {
      console.error('Error loading referral program status:', error);
      setReferralProgramActive(false);
    }
  };

  const handleCreateCustomerSource = async () => {
    if (!selectedBusiness?.id || !newSourceName.trim()) return;
    
    try {
      setCreateSourceLoading(true);
      const newSource = await CustomerSourcesService.createCustomerSource({
        business_id: selectedBusiness.id,
        name: newSourceName.trim()
      });
      
      // Add to list and select it
      setCustomerSources(prev => [...prev, newSource].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedCustomerSource(newSource);
      
      // Close modal and reset
      setIsCreateSourceModalOpen(false);
      setNewSourceName('');
    } catch (error) {
      console.error('Error creating customer source:', error);
      alert(error instanceof Error ? error.message : 'Failed to create customer source');
    } finally {
      setCreateSourceLoading(false);
    }
  };

  // Auto-detect customer source based on repeat status and booking history
  const detectCustomerSource = async (customer: Customer) => {
    if (!selectedBusiness?.id) return;
    
    try {
      // Step 1: Check customer.repeat column first (optimization)
      if (customer.repeat === true) {
        // Customer is already marked as repeat, auto-select repeat source
        setSelectedCustomerSource({ 
          id: 'repeat-customer', 
          business_id: selectedBusiness.id,
          name: 'Repeat Customer', 
          is_active: true,
          created_at: '',
          updated_at: ''
        });
        return;
      }
      
      // Step 2: If customer.repeat = false, count their total bookings (all statuses)
      if (!supabase) return;
      
      const { data: bookingsData, error } = await supabase
        .from('bookings')
        .select('id')
        .eq('customer_id', customer.id)
        .eq('business_id', selectedBusiness.id);
      
      if (error) {
        console.error('Error counting customer bookings:', error);
        return;
      }
      
      const totalBookings = bookingsData?.length || 0;
      
      // If customer has 1+ bookings, this will be their 2nd+ booking (repeat)
      // We also need to update their repeat status in the database
      if (totalBookings >= 1) {
        setSelectedCustomerSource({ 
          id: 'repeat-customer', 
          business_id: selectedBusiness.id,
          name: 'Repeat Customer', 
          is_active: true,
          created_at: '',
          updated_at: ''
        });
        
        // Update customer's repeat column to true since this will be their 2nd+ booking
        try {
          const { error: updateError } = await supabase
            .from('customers')
            .update({ 
              repeat: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', customer.id);
          
          if (updateError) {
            console.error('Error updating customer repeat status:', updateError);
          } else {
            console.log(`✅ Updated customer ${customer.first_name} ${customer.last_name} to repeat status`);
            // Update local customer state
            setSelectedCustomer(prev => prev ? { ...prev, repeat: true } : null);
          }
        } catch (updateError) {
          console.error('Error updating customer repeat status:', updateError);
        }
      } else {
        // Customer has 0 bookings, this will be their 1st (new customer)
        setSelectedCustomerSource(null);
      }
      
    } catch (error) {
      console.error('Error detecting customer source:', error);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Reset all form fields
      setTitle('Booking');
      setSelectedCustomer(null);
      setCurrentVehicle(null);
      setSelectedServices([]);
      setSelectedResources([]);
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setBookingColor('#3b82f6');
      setBookingSecondaryColor(null);
      setError(null);
      setIsTitleExpanded(false);
      setIsCustomerSectionExpanded(true);
      setIsServicesSectionExpanded(true);
      setIsResourcesExpanded(false);
      setSchedulePickerOpen(false);
      setIsUnscheduled(false);
      setExpandedServiceDescriptions(new Set());
      setServiceHasAddons({});
      setPendingDiscount(null);
      setIsDiscountModalOpen(false);

      // This is now a location-only booking modal
      setIsLocationBooking(true);
    }
  };

  const handleVehicleCreated = async (vehicle: any) => {
    // Update customer data to get the new vehicle
    const updatedCustomer = await getCustomer(selectedCustomer?.id || '');
    setSelectedCustomer(updatedCustomer);
    
    // Set the new vehicle as current and open service selection
    setCurrentVehicle(vehicle);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
    
    // Save progress after vehicle creation
    saveBookingProgress();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      if (!selectedCustomer || !selectedBusiness || selectedServices.length === 0) {
        throw new Error('Please select a customer, business, and at least one service');
      }

      // Field-quote link gate: before creating, surface any field quotes that
      // likely belong to this booking and let the user confirm/pick. Skipped when
      // the origin is already known (proposal conversion, explicit field-quote
      // convert) or for warranty bookings.
      if (
        resolvedFieldQuoteIdsRef.current === null &&
        !initialProposal &&
        !initialFieldQuoteId &&
        !isWarrantyMode
      ) {
        const candidates = await resolveCandidateFieldQuotes({
          businessId: selectedBusiness.id,
          customerId: selectedCustomer.id,
          opportunityId: initialOpportunityId ?? null,
          projectId: null,
        });
        if (candidates.length > 0) {
          setFqCandidates(candidates);
          // Pre-select the highest-confidence match (proposal/opportunity first).
          setFqSelectedIds([candidates[0].id]);
          setFqPickerOpen(true);
          setLoading(false);
          return; // wait for the user's choice, then they re-submit via the picker
        }
        resolvedFieldQuoteIdsRef.current = [];
      }

      console.log('🔍 [CreateBookingModal] Form validation - selectedCustomerSource:', selectedCustomerSource);
      console.log('🔍 [CreateBookingModal] Form validation - isWarrantyMode:', isWarrantyMode);
      
      // For warranty bookings, skip customer source validation since it's auto-set
      if (isWarrantyMode) {
        console.log('🛡️ [CreateBookingModal] Warranty mode - skipping customer source validation');
        // Use warranty as the customer source
        const warrantyCustomerSource = { 
          id: 'warranty', 
          business_id: selectedBusiness?.id || '',
          name: 'Warranty', 
          is_active: true,
          created_at: '',
          updated_at: ''
        };
        // Update the state for consistency
        setSelectedCustomerSource(warrantyCustomerSource);
      } else if (!selectedCustomerSource) {
        throw new Error('Please select a customer source');
      }

      // Removed test trigger to prevent duplicate automation executions
      // The real trigger runs after booking creation with actual data

      // Now continue with normal booking creation flow...
      console.log('📝 Proceeding with actual booking creation...');

      // Get total bookings count for customer repeat status determination
      let totalBookings = 0;
      if (selectedCustomer.repeat === false) {
        // Only count if customer is not already marked as repeat
        const { data: bookingsData } = await supabase
          .from('bookings')
          .select('id')
          .eq('customer_id', selectedCustomer.id)
          .eq('business_id', selectedBusiness.id);
        totalBookings = bookingsData?.length || 0;
      }

      // Determine customer repeat status for this booking
      const customerRepeatStatus = selectedCustomer.repeat === true ? 'repeat' : 
        (totalBookings >= 1 ? 'repeat' : 'new');
      
      // Get selected customer source name (or undefined if none selected)
      // For warranty bookings, always use "Warranty" as the source
      const customerSourceName = isWarrantyMode ? 'Warranty' : (selectedCustomerSource ? selectedCustomerSource.name : undefined);

      // Debug logging for warranty booking creation
      if (isWarrantyMode) {
        console.log('🛡️ [CreateBookingModal] About to create warranty booking with data:', {
          is_warranty: isWarrantyMode,
          original_booking_id: (() => {
            if (!isWarrantyMode) return undefined;
            
            console.log('🛡️ [CreateBookingModal] Setting original_booking_id for warranty booking');
            console.log('🛡️ [CreateBookingModal] isWarrantyMode:', isWarrantyMode);
            console.log('🛡️ [CreateBookingModal] originalBookingId prop:', originalBookingId);
            
            // Try to get original booking ID from localStorage first (from warranty data)
            const storedWarrantyData = localStorage.getItem('warrantyBookingData');
            console.log('🛡️ [CreateBookingModal] storedWarrantyData from localStorage:', storedWarrantyData);
            
            if (storedWarrantyData) {
              try {
                const warrantyData = JSON.parse(storedWarrantyData);
                console.log('🛡️ [CreateBookingModal] Parsed warranty data:', warrantyData);
                console.log('🛡️ [CreateBookingModal] originalBookingId from localStorage:', warrantyData.originalBookingId);
                return warrantyData.originalBookingId;
              } catch (error) {
                console.error('Error parsing warranty data for original_booking_id:', error);
              }
            }
            
            // Try to get original booking ID from localStorage (set by App component)
            const storedOriginalBookingId = localStorage.getItem('warrantyOriginalBookingId');
            console.log('🛡️ [CreateBookingModal] storedOriginalBookingId from localStorage:', storedOriginalBookingId);
            
            if (storedOriginalBookingId) {
              console.log('🛡️ [CreateBookingModal] Using originalBookingId from App localStorage:', storedOriginalBookingId);
              return storedOriginalBookingId;
            }
            
            // Fallback to prop
            console.log('🛡️ [CreateBookingModal] Using originalBookingId prop as fallback:', originalBookingId);
            return originalBookingId;
          })()
        });
      }

      // Build the params once — recurring series reuse them with shifted dates.
      const bookingParams: Parameters<typeof createBooking>[0] = {
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
        start_date: isUnscheduled ? undefined : scheduleData.startDate,
        start_time: isUnscheduled ? undefined : scheduleData.startTime,
        end_date: isUnscheduled ? undefined : scheduleData.endDate,
        end_time: isUnscheduled ? undefined : scheduleData.endTime,
        drop_off_date: isUnscheduled ? undefined : scheduleData.dropOffDate,
        drop_off_time: isUnscheduled ? undefined : scheduleData.dropOffTime,
        status: 'scheduled',
        is_unscheduled: isUnscheduled,
        notes: `Created with ${selectedServices.length} services`,
        customer_notes: scheduleData.customer_notes || "",
        color: bookingColor,
        secondary_color: bookingSecondaryColor || undefined,
        customer_repeat_status: customerRepeatStatus,
        source: customerSourceName,
        referral_source_customer_id: referralSourceCustomer?.id || undefined,
        created_by: currentResource?.id || 'admin',
        // Add project_id if this is from a project
        project_id: ((sourceProposal || initialProposal) as any)?.isProject
          ? ((sourceProposal || initialProposal) as any).project_id
          : null,
        // Add pipeline_opportunities_id if this is from an opportunity
        // Check both initialOpportunityId prop and linked proposal's pipeline_opportunities_id
        pipeline_opportunities_id:
          initialOpportunityId
          || ((sourceProposal || initialProposal) as any)?.pipeline_opportunities_id
          || null,
        // Proposal origin → lets the invoice engine adopt the proposal's deposit invoice
        source_proposal_id: (() => {
          const linked = sourceProposal || initialProposal;
          return (linked && !(linked as any)?.isProject) ? linked.id : null;
        })(),
        // Exclude this booking from all automations when checked
        automations_disabled: removeFromAutomations,
        // Field quotes the user confirmed in the pre-create picker
        field_quote_ids: resolvedFieldQuoteIdsRef.current && resolvedFieldQuoteIdsRef.current.length > 0
          ? resolvedFieldQuoteIdsRef.current
          : undefined,
        // Warranty booking fields
        is_warranty: isWarrantyMode,
        original_booking_id: (() => {
          if (!isWarrantyMode) return undefined;
          
          console.log('🛡️ [CreateBookingModal] Setting original_booking_id for warranty booking');
          console.log('🛡️ [CreateBookingModal] isWarrantyMode:', isWarrantyMode);
          console.log('🛡️ [CreateBookingModal] originalBookingId prop:', originalBookingId);
          
          // Try to get original booking ID from localStorage first (from warranty data)
          const storedWarrantyData = localStorage.getItem('warrantyBookingData');
          console.log('🛡️ [CreateBookingModal] storedWarrantyData from localStorage:', storedWarrantyData);
          
          if (storedWarrantyData) {
            try {
              const warrantyData = JSON.parse(storedWarrantyData);
              console.log('🛡️ [CreateBookingModal] Parsed warranty data:', warrantyData);
              console.log('🛡️ [CreateBookingModal] originalBookingId from localStorage:', warrantyData.originalBookingId);
              return warrantyData.originalBookingId;
            } catch (error) {
              console.error('Error parsing warranty data for original_booking_id:', error);
            }
          }
          
          // Try to get original booking ID from localStorage (set by App component)
          const storedOriginalBookingId = localStorage.getItem('warrantyOriginalBookingId');
          console.log('🛡️ [CreateBookingModal] storedOriginalBookingId from localStorage:', storedOriginalBookingId);
          
          if (storedOriginalBookingId) {
            console.log('🛡️ [CreateBookingModal] Using originalBookingId from App localStorage:', storedOriginalBookingId);
            return storedOriginalBookingId;
          }
          
          // Fallback to prop
          console.log('🛡️ [CreateBookingModal] Using originalBookingId prop as fallback:', originalBookingId);
          return originalBookingId;
        })(),
        // Group services by vehicle
        vehicles: Object.entries(servicesByVehicle).map(([vehicleId, services]) => ({
          // Convert placeholder keys and string "null" back to actual null for database
          vehicle_id: (vehicleId === 'no-vehicle' || vehicleId === 'no-vehicle-placeholder' || vehicleId === 'null' || vehicleId === null) ? null : vehicleId,
          services: services.map(service => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : (service.service.tax_rate || 5);
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            
            return {
              service_id: service.service.id,
              name: service.service.name,
              description: service.service.description || '',
              price: servicePrice,
              // Carry per-line discount (e.g. from a proposal) → booking_services → invoice line discount
              discount_type: service.discount_type === 'percentage' ? 'percentage' : 'fixed',
              discount_value: service.discount_value || 0,
              discount_amount: service.discount_amount || 0,
              tax_exempt: service.service.tax_exempt || false,
              tax_rate: taxRate,
              tax_amount: taxAmount,
              total_amount: taxableAmount + taxAmount,
              duration_minutes: service.priceOption.duration_minutes,
              pricing_type: service.priceOption.pricing_type || 'flat',
              unit_price: service.priceOption.unit_price ?? null,
              duration_per_unit: service.priceOption.duration_per_unit ?? null,
              quantity: service.priceOption.quantity ?? 0,
              compare_at_price: service.priceOption.compare_at_price ?? null,
              workflow_steps: (() => {
                const workflowData = service.service.workflowData || null;
                if (workflowData) {
                  console.log(`📋 Saving workflow data for service "${service.service.name}":`, workflowData);
                }
                return workflowData;
              })()
            };
          })
        })),
        // Add selected resources if any
        resources: selectedResources.map(resource => ({
          resource_id: resource.id,
          role: 'technician'
        })),
        // Assets picked from the week-view schedule grid
        asset_ids: selectedBookingAssets.length > 0 ? selectedBookingAssets.map(a => a.id) : undefined,
        // Workflow assignments will be added with JSONB approach
      };

      // Create the booking first
      const result = await createBooking(bookingParams);

      if (!result || !result.id) throw new Error('No booking data returned');

      // Link the booking to an originating field quote (e.g. "Convert to booking")
      if (initialFieldQuoteId) {
        await linkFieldQuotesToBooking(result.id, [initialFieldQuoteId], selectedBusiness.id);
      }

      // Apply referral discount if conditions are met
      if (
        referralSourceCustomer &&
        selectedCustomerSource?.name.toLowerCase() === 'referral' &&
        selectedCustomer &&
        !selectedCustomer.repeat // Customer is new
      ) {
        try {
          console.log('🎁 Applying referral discount to booking:', result.id);
          const discountResult = await applyReferralDiscount(result.id, selectedBusiness.id);
          if (discountResult.success) {
            console.log('✅ Referral discount applied successfully');
          } else {
            console.warn('⚠️ Failed to apply referral discount:', discountResult.error);
            // Don't throw - booking is already created, discount is supplementary
          }
        } catch (discountError) {
          console.error('❌ Error applying referral discount:', discountError);
          // Don't throw - booking is already created, discount is supplementary
        }
      }

      // Apply pending discount if set
      if (pendingDiscount && result.id) {
        try {
          await applyDiscount(result.id, pendingDiscount.title, pendingDiscount.type, pendingDiscount.amount);
          console.log('✅ Pending discount applied:', pendingDiscount);
        } catch (discountErr) {
          console.error('Error applying discount:', discountErr);
        }
      }

      // Cast result to access project_id (if present)
      const bookingWithProjectId = result as any;

      // Insert add-ons and workflow data for each service (shared with the
      // recurring-series children, which need the same rows per occurrence).
      const applyAddonsAndWorkflows = async (bookingId: string) => {
        for (const serviceData of selectedServices) {
          const addons = selectedServiceAddons[serviceData.service.id] || [];
          const workflowData = serviceData.service.workflowData;

          if (addons.length > 0 || workflowData) {
            const { data: serviceResult } = await supabase
              .from('booking_services')
              .select('id')
              .eq('booking_id', bookingId)
              .eq('service_id', serviceData.service.id)
              .single();

            if (serviceResult) {
              // Insert add-ons
              if (addons.length > 0) {
                const { error: addonsError } = await supabase
                  .from('booking_service_addons')
                  .insert(
                    addons.map(addon => ({
                      booking_service_id: serviceResult.id,
                      addon_id: addon.id,
                      price: addon.price
                    }))
                  );

                if (addonsError) throw addonsError;
              }

              // Insert workflow data to JSONB column
              if (workflowData && workflowData.length > 0) {
                const { error: workflowError } = await supabase
                  .from('booking_services')
                  .update({
                    workflow_steps: workflowData
                  })
                  .eq('id', serviceResult.id);

                if (workflowError) {
                  console.error('Error saving workflow data:', workflowError);
                  throw workflowError;
                }

                console.log(`Workflow data saved for service ${serviceData.service.name}:`, workflowData);
              }
            }
          }
        }
      };
      await applyAddonsAndWorkflows(result.id);

      // Products: stock deduction + billing row PER PRODUCT, so a product whose
      // stock deduction fails (e.g. insufficient stock) is never billed, and the
      // user is told instead of the failure being silently swallowed.
      if (selectedProducts.length > 0) {
        const failedProducts: string[] = [];

        for (const productSelection of selectedProducts) {
          try {
            await createStockTransaction({
              item_id: productSelection.product.id,
              type: 'sale',
              quantity: productSelection.product.quantity,
              price: productSelection.product.acquisition_cost,
              notes: `Booking: ${result.id} - ${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
              booking_id: result.id
            });
          } catch (stockError) {
            console.error(`❌ Stock deduction failed for ${productSelection.product.name}:`, stockError);
            failedProducts.push(
              `${productSelection.product.name} — ${stockError instanceof Error ? stockError.message : 'stock deduction failed'}`,
            );
            continue; // don't bill a product that didn't leave stock
          }

          const { error: productSaleError } = await supabase
            .from('product_booking_sales')
            .insert({
              business_id: selectedBusiness?.id,
              booking_id: result.id,
              stock_item_id: productSelection.product.id,
              product_name: productSelection.product.name,
              quantity: productSelection.product.quantity,
              unit_price: productSelection.product.price,
              total_price: productSelection.product.price * productSelection.product.quantity
            });

          if (productSaleError) {
            console.error(`❌ Error creating product_booking_sales entry for ${productSelection.product.name}:`, productSaleError);
            // Billing row failed after stock moved — put the stock back so the
            // ledger and billing never disagree.
            await supabase
              .from('stock_transactions')
              .delete()
              .eq('booking_id', result.id)
              .eq('item_id', productSelection.product.id)
              .eq('type', 'sale');
            failedProducts.push(`${productSelection.product.name} — could not add to booking billing`);
          }
        }

        if (failedProducts.length > 0) {
          alert(
            `The booking was created, but ${failedProducts.length === 1 ? 'this product' : 'these products'} could not be added:\n\n${failedProducts.join('\n')}\n\nAdd ${failedProducts.length === 1 ? 'it' : 'them'} from the booking after adjusting stock.`,
          );
        }
      }

      // Execute scheduled triggers after booking is created
      // (skipped entirely when the booking is excluded from automations)
      if (selectedBusiness?.id && !removeFromAutomations) {
        try {
          // Extract customer information
          const customerInfo = {
            name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            firstName: selectedCustomer.first_name,
            lastName: selectedCustomer.last_name,
            phone: selectedCustomer.phone || undefined,
            email: selectedCustomer.email || undefined,
            booking: {
              dropOffDate: scheduleData.dropOffDate || undefined,
              dropOffTime: scheduleData.dropOffTime || undefined,
              startDate: scheduleData.startDate || undefined,
              startTime: scheduleData.startTime || undefined,
              endDate: scheduleData.endDate || undefined,
              endTime: scheduleData.endTime || undefined
            }
          };

          // This runs in the background and doesn't block UI
          executeScheduledTriggers(selectedBusiness.id, customerInfo)
            .catch(triggerError => console.error('Error executing scheduled triggers:', triggerError));
          
          console.log('Scheduled triggers initiated for new booking');
        } catch (triggerError) {
          console.error('Failed to execute scheduled triggers:', triggerError);
          // We don't throw here to avoid blocking the booking creation flow
        }


      }

      // Removed all persistence systems
      // Note: Persistence now handled centrally by usePageLifecycle

      // Trigger automations for real booking creation
      try {
        console.log('🤖 REAL BOOKING: Triggering booking automations for created event...');
        
        // Build comprehensive booking data for automation
        const realBookingData = {
          booking_id: result.id, // Use actual UUID
          booking_number: result.booking_number, // Also include booking number for display
          title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
          grand_total: selectedServices.reduce((total, service) => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : (service.service.tax_rate || 5);
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            return total + taxableAmount + taxAmount;
          }, 0),
          
          // Customer information
          customer: {
            id: selectedCustomer.id,
            name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            full_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            first_name: selectedCustomer.first_name,
            last_name: selectedCustomer.last_name,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone
          },
          
          // Schedule information
          schedule: {
            start_date: scheduleData.startDate,
            start_time: scheduleData.startTime,
            end_date: scheduleData.endDate,
            end_time: scheduleData.endTime,
            drop_off_date: scheduleData.dropOffDate,
            drop_off_time: scheduleData.dropOffTime
          },
          
          // Services information
          services: selectedServices.map(service => ({
            id: service.service.id,
            name: service.service.name,
            price: service.priceOption.price,
            duration_minutes: service.priceOption.duration_minutes,
            vehicle: service.vehicle ? {
              id: service.vehicle.id,
              make: service.vehicle.make,
              model: service.vehicle.model,
              year: service.vehicle.year,
              color: service.vehicle.color
            } : null,
            addons: selectedServiceAddons[service.service.id] || []
          })),
          
          // Resources assigned
          resources: selectedResources.map(resource => ({
            id: resource.id,
            name: resource.name,
            phone: resource.phone,
            role: 'technician'
          })),
          
          // Vehicle summary for easy access
          vehicles: (() => {
            const vehicles = selectedServices
              .filter(service => service.vehicle !== null)
              .map(service => ({
                make: service.vehicle!.make || '',
                model: service.vehicle!.model || '',
                year: service.vehicle!.year || 0,
                color: service.vehicle!.color || '',
                display: `${service.vehicle!.year || ''} ${service.vehicle!.make || ''} ${service.vehicle!.model || ''}`.trim()
              }));
            
            // Remove duplicates based on make/model/year combination
            const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
              index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
            );
            
            return uniqueVehicles;
          })(),
          
          // Additional booking details
          notes: scheduleData.customer_notes || null,
          color: bookingColor,
          secondary_color: bookingSecondaryColor || null,
          created_at: new Date().toISOString(),
          status: 'scheduled'
        };

        console.log('📋 Real booking data:', realBookingData);
        
        // Trigger automation (non-blocking). Unscheduled bookings skip the
        // "created" trigger — it fires later when the booking gets a date.
        if (isUnscheduled) {
          console.log('🤖 REAL BOOKING: Unscheduled booking — skipping created automations');
        } else {
          triggerBookingAutomations(selectedBusiness.id, 'created', realBookingData)
            .then(() => console.log('🤖 REAL BOOKING: Booking automations completed successfully'))
            .catch(automationError => console.error('❌ REAL BOOKING: Error triggering booking automations:', automationError));
          
          console.log('🤖 REAL BOOKING: Booking automation trigger initiated');
        }
      } catch (automationError) {
        console.error('❌ REAL BOOKING: Failed to trigger booking automations:', automationError);
        // We don't throw here to avoid blocking the booking creation flow
      }

      // Recurring booking series — fan out the remaining occurrences at the
      // same time-of-day for the chosen length (e.g. monthly for 1 year).
      if (isRecurring && !isUnscheduled && !isWarrantyMode && scheduleData.startDate && scheduleData.startTime) {
        try {
          const seriesResult = await createBookingSeries({
            businessId: selectedBusiness.id,
            customerId: selectedCustomer.id,
            firstBookingId: result.id,
            baseParams: bookingParams,
            frequency: recurringFrequency,
            lengthMonths: recurringLengthMonths,
            isMobile: false,
          });
          if (seriesResult) {
            const dayDelta = (from: string, to: string) =>
              Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000);
            const shiftYmd = (ymd: string | undefined | null, days: number): string | undefined => {
              if (!ymd) return undefined;
              const [y, m, d] = ymd.split('-').map(Number);
              const dt = new Date(y, m - 1, d + days);
              return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
            };

            for (const child of seriesResult.children) {
              try {
                await applyAddonsAndWorkflows(child.id);
              } catch (extraErr) {
                console.error('Series child add-ons failed:', extraErr);
              }
              // Children fire 'created' with series_child: reminder waits still
              // schedule per visit, but instant confirmations are skipped
              // (handled in execute-automation-step) — one confirmation total.
              if (!removeFromAutomations) {
                const delta = dayDelta(scheduleData.startDate, child.start_date);
                triggerBookingAutomations(selectedBusiness.id, 'created', {
                  booking_id: child.id,
                  series_child: true,
                  title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
                  customer: {
                    id: selectedCustomer.id,
                    name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
                    full_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
                    first_name: selectedCustomer.first_name,
                    last_name: selectedCustomer.last_name,
                    email: selectedCustomer.email,
                    phone: selectedCustomer.phone
                  },
                  schedule: {
                    start_date: child.start_date,
                    start_time: scheduleData.startTime,
                    end_date: shiftYmd(scheduleData.endDate, delta),
                    end_time: scheduleData.endTime,
                    drop_off_date: shiftYmd(scheduleData.dropOffDate, delta),
                    drop_off_time: scheduleData.dropOffTime
                  },
                  services: selectedServices.map(service => ({
                    id: service.service.id,
                    name: service.service.name,
                    price: service.priceOption.price,
                    duration_minutes: service.priceOption.duration_minutes
                  })),
                  status: 'scheduled'
                }).catch(err => console.error('Series child automations failed:', err));
              }
            }

            const fmtDate = (ymd: string) =>
              new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            toast({
              title: `Recurring booking: ${seriesResult.totalVisits} visits scheduled`,
              description: [
                `Repeats through ${fmtDate(seriesResult.lastDate)}.`,
                seriesResult.conflictDates.length > 0
                  ? `Heads up — these dates are double-booked: ${seriesResult.conflictDates.map(fmtDate).join(', ')}.`
                  : '',
                seriesResult.failedDates.length > 0
                  ? `Could not create: ${seriesResult.failedDates.map(fmtDate).join(', ')}.`
                  : ''
              ].filter(Boolean).join(' ')
            });
          }
        } catch (seriesError) {
          console.error('Failed to create recurring series:', seriesError);
          toast({
            title: 'Recurring series failed',
            description: 'The first booking was created, but the repeat visits could not be scheduled.'
          });
        }
      }

      // Update proposal status to 'booked' if this was created from a proposal (not a project)
      const linkedProposal = sourceProposal || initialProposal;
      if (linkedProposal?.id && !(linkedProposal as any)?.isProject) {
        try {
          const { error: proposalUpdateError } = await supabase
            .from('proposals')
            .update({ status: 'booked', ...(result?.id ? { booking_id: result.id } : {}) })
            .eq('id', linkedProposal.id);

          if (proposalUpdateError) {
            console.error('❌ Failed to update proposal status to booked:', proposalUpdateError);
          } else {
            console.log('✅ Updated proposal status to booked:', linkedProposal.id);
          }
        } catch (error) {
          console.error('❌ Error updating proposal status:', error);
        }

        // Link the new booking to the field quote that originated this proposal
        if (result?.id) {
          try {
            const { data: proposalRow } = await supabase
              .from('proposals')
              .select('field_quote_id')
              .eq('id', linkedProposal.id)
              .maybeSingle();
            if (proposalRow?.field_quote_id) {
              await linkFieldQuotesToBooking(result.id, [proposalRow.field_quote_id], selectedBusiness.id);
            }
          } catch (linkErr) {
            console.error('❌ Error linking field quote to booking:', linkErr);
          }
        }

        // Apply proposal deposits to the new booking
        if (result?.id) {
          console.log('🔄 Applying proposal deposits to booking:', {
            proposalId: linkedProposal.id,
            bookingId: result.id
          });
          
          // Find all payments for this proposal (including pending deposits)
          console.log('🔍 Searching for payments with proposal_id:', linkedProposal.id);
          const { data: proposalPayments, error: paymentsError } = await supabase
            .from('payments')
            .select('*')
            .eq('proposal_id', linkedProposal.id);
          
          console.log('🔍 Payment query result:', {
            error: paymentsError,
            count: proposalPayments?.length || 0,
            payments: proposalPayments?.map(p => ({
              id: p.id,
              proposal_id: p.proposal_id,
              booking_id: p.booking_id,
              payment_type: p.payment_type,
              status: p.status,
              amount: p.amount
            }))
          });
          
          if (paymentsError) {
            console.error('❌ Error fetching proposal payments:', paymentsError);
          } else {
            if (proposalPayments && proposalPayments.length > 0) {
              console.log(`📦 Found ${proposalPayments.length} payment(s) to transfer to booking ${result.id}`);
              
              // Update each payment to link it to the booking
              for (const payment of proposalPayments) {
                console.log(`🔄 Processing payment ${payment.id}:`, {
                  current_booking_id: payment.booking_id,
                  current_proposal_id: payment.proposal_id,
                  payment_type: payment.payment_type,
                  status: payment.status,
                  amount: payment.amount
                });
                
                // Preserve payment_type and all other fields, only update booking_id
                const updateData: any = { 
                  booking_id: result.id,
                };
                
                // Explicitly preserve payment_type if it exists
                if (payment.payment_type) {
                  updateData.payment_type = payment.payment_type;
                }
                
                console.log(`🔄 Updating payment ${payment.id} with:`, updateData);
                
                const { error: updatePaymentError, data: updatedPayment } = await supabase
                  .from('payments')
                  .update(updateData)
                  .eq('id', payment.id)
                  .select()
                  .single();
                
                if (updatePaymentError) {
                  console.error(`❌ Error updating payment ${payment.id}:`, updatePaymentError);
                  continue;
                }
                
                if (!updatedPayment) {
                  console.error(`❌ Update returned no data for payment ${payment.id}`);
                  // Verify by querying the payment again
                  const { data: verifyPayment, error: verifyError } = await supabase
                    .from('payments')
                    .select('booking_id, proposal_id, payment_type, status')
                    .eq('id', payment.id)
                    .single();
                  
                  if (verifyError) {
                    console.error(`❌ Could not verify payment update:`, verifyError);
                  } else {
                    console.log(`🔍 Verification query result:`, verifyPayment);
                  }
                  continue;
                }
                
                // Verify the update actually worked
                console.log(`✅ Update response for payment ${payment.id}:`, {
                  paymentType: payment.payment_type || 'final',
                  status: payment.status || 'pending',
                  amount: payment.amount,
                  old_booking_id: payment.booking_id,
                  new_booking_id: updatedPayment.booking_id,
                  updatedPayment: updatedPayment
                });
                
                if (updatedPayment.booking_id !== result.id) {
                  console.error(`❌ CRITICAL: Update did not set booking_id correctly!`, {
                    expected: result.id,
                    actual: updatedPayment.booking_id,
                    paymentId: payment.id
                  });
                  
                  // Try to update again with explicit error handling
                  console.log(`🔄 Retrying update for payment ${payment.id}...`);
                  const { error: retryError, data: retryUpdated } = await supabase
                    .from('payments')
                    .update({ booking_id: result.id })
                    .eq('id', payment.id)
                    .select('booking_id')
                    .single();
                  
                  if (retryError) {
                    console.error(`❌ Retry also failed:`, retryError);
                  } else {
                    console.log(`✅ Retry result:`, retryUpdated);
                  }
                } else {
                  console.log(`✅ Confirmed: Payment ${payment.id} now has booking_id = ${result.id}`);
                }
              }
            } else {
              console.log('ℹ️ No payments found for this proposal');
            }
          }
        } else {
          console.error('❌ Cannot apply deposits - missing booking ID:', {
            hasProposalId: !!(sourceProposal || initialProposal)?.id,
            hasBookingId: !!result?.id,
            result: result
          });
        }
      }

      // Handle project conversion if booking was created from a project
      if (bookingWithProjectId?.id && bookingWithProjectId.project_id) {
        console.log('🔄 Handling project to booking conversion:', {
          projectId: bookingWithProjectId.project_id,
          bookingId: bookingWithProjectId.id
        });

        try {
          // Update project with booking_id and status
          const { error: projectUpdateError } = await supabase
            .from('projects')
            .update({ 
              booking_id: bookingWithProjectId.id,
              status: 'Converted to booking'
            })
            .eq('id', bookingWithProjectId.project_id);

          if (projectUpdateError) {
            console.error('❌ Error updating project with booking_id:', projectUpdateError);
          } else {
            console.log('✅ Updated project with booking_id:', bookingWithProjectId.project_id);
          }

          // Transfer project payments to booking
          const { data: projectPayments, error: paymentsError } = await supabase
            .from('payments')
            .select('*')
            .eq('project_id', bookingWithProjectId.project_id);

          if (paymentsError) {
            console.error('❌ Error fetching project payments:', paymentsError);
          } else if (projectPayments && projectPayments.length > 0) {
            console.log(`📦 Found ${projectPayments.length} payment(s) to transfer to booking ${bookingWithProjectId.id}`);

            for (const payment of projectPayments) {
              const updateData: any = {
                booking_id: bookingWithProjectId.id,
              };

              if (payment.payment_type) {
                updateData.payment_type = payment.payment_type;
              }

              const { error: updatePaymentError } = await supabase
                .from('payments')
                .update(updateData)
                .eq('id', payment.id);

              if (updatePaymentError) {
                console.error(`❌ Error updating payment ${payment.id}:`, updatePaymentError);
              } else {
                console.log(`✅ Linked payment ${payment.id} to booking ${bookingWithProjectId.id}`);
              }
            }
          }

          // Link project materials to booking (update materials to have both project_id and booking_id)
          const { data: projectMaterials, error: materialsError } = await supabase
            .from('booking_materials')
            .select('id, item_id, booking_id')
            .eq('project_id', bookingWithProjectId.project_id)
            .is('booking_id', null); // Only get materials that aren't already linked to a booking

          if (materialsError) {
            console.error('❌ Error fetching project materials:', materialsError);
          } else if (projectMaterials && projectMaterials.length > 0) {
            console.log(`📦 Found ${projectMaterials.length} material(s) to link to booking ${bookingWithProjectId.id}`);

            for (const material of projectMaterials) {
              // Update material to have both project_id and booking_id
              const { error: updateMaterialError } = await supabase
                .from('booking_materials')
                .update({ booking_id: bookingWithProjectId.id })
                .eq('id', material.id);

              if (updateMaterialError) {
                console.error(`❌ Error updating material ${material.id}:`, updateMaterialError);
              } else {
                console.log(`✅ Linked material ${material.id} to booking ${bookingWithProjectId.id}`);

                // If this is an inventory item, also update stock_transactions to include booking_id
                if (material.item_id) {
                  // Find stock transactions for this project and item that don't have booking_id
                  const { data: stockTransactions, error: stockError } = await supabase
                    .from('stock_transactions')
                    .select('id')
                    .eq('item_id', material.item_id)
                    .eq('type', 'sale')
                    .is('booking_id', null)
                    .like('notes', `%project: ${bookingWithProjectId.project_id}%`);

                  if (!stockError && stockTransactions && stockTransactions.length > 0) {
                    // Update stock transactions to include booking_id
                    const { error: updateStockError } = await supabase
                      .from('stock_transactions')
                      .update({ booking_id: bookingWithProjectId.id })
                      .in('id', stockTransactions.map(t => t.id));

                    if (updateStockError) {
                      console.error(`❌ Error updating stock transactions for material ${material.id}:`, updateStockError);
                    } else {
                      console.log(`✅ Updated ${stockTransactions.length} stock transaction(s) for material ${material.id}`);
                    }
                  }
                }
              }
            }
          }

          // Transfer project expenses to booking (update expenses to have both project_id and booking_id)
          const { data: projectExpenses, error: expensesError } = await supabase
            .from('expenses')
            .select('id')
            .eq('project_id', bookingWithProjectId.project_id)
            .is('booking_id', null); // Only get expenses that aren't already linked to a booking

          if (expensesError) {
            console.error('❌ Error fetching project expenses:', expensesError);
          } else if (projectExpenses && projectExpenses.length > 0) {
            console.log(`💰 Found ${projectExpenses.length} expense(s) to link to booking ${bookingWithProjectId.id}`);

            for (const expense of projectExpenses) {
              // Update expense to have both project_id and booking_id
              const { error: updateExpenseError } = await supabase
                .from('expenses')
                .update({ booking_id: bookingWithProjectId.id })
                .eq('id', expense.id);

              if (updateExpenseError) {
                console.error(`❌ Error updating expense ${expense.id}:`, updateExpenseError);
              } else {
                console.log(`✅ Linked expense ${expense.id} to booking ${bookingWithProjectId.id}`);
              }
            }
          }
        } catch (error) {
          console.error('❌ Error handling project conversion:', error);
        }
      }

      // Emit custom event to notify other components (like CalendarV2) immediately
      window.dispatchEvent(new CustomEvent('bookingCreated', { 
        detail: { bookingId: result.id, businessId: selectedBusiness.id, projectId: bookingWithProjectId?.project_id } 
      }));
      console.log('📅 Dispatched bookingCreated event for immediate calendar update');

      // Clear warranty booking data from localStorage after successful creation
      if (isWarrantyMode) {
        console.log('🛡️ [CreateBookingModal] Clearing warranty data from localStorage after successful booking creation');
        localStorage.removeItem('warrantyBookingData');
        localStorage.removeItem('warrantyOriginalBookingId');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to create booking. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async (
    service: ExtendedService,
    priceOption: any,
    // AI assistant adds pass the resolved vehicle explicitly and suppress the
    // follow-up workflow/addons popups (silent), since the AI reports addon
    // availability in its own channel instead.
    opts?: { vehicle?: CustomerVehicle | null; silent?: boolean },
  ) => {
    // Check industry - for Home Services, vehicle is optional
    const isHomeServices = selectedBusiness?.industry === 'Home Services';
    const vehicle = opts && 'vehicle' in opts ? (opts.vehicle ?? null) : currentVehicle;

    if (!isHomeServices && !vehicle) {
      // For Automotive businesses, vehicle is required
      return;
    }

    // Generate unique instance ID for this service instance
    const instanceId = `${service.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const newService: ServiceSelection = {
      instanceId, // Unique identifier for this service instance
      vehicle,
      service: {
        ...service,
        tax_rate: service.tax_rate || 5
      },
      priceOption
    };

    // Calculate new services array
    const newServicesArray = [...selectedServices, newService];
    setSelectedServices(newServicesArray);
    
    // Save progress with new services array immediately - bypass timing issues
    saveBookingProgress({ selectedServices: newServicesArray });
    
    // Check if service has a workflow template (only if workflows power-up is enabled)
    if (workflowsEnabled && !opts?.silent) {
      try {
        const { data: serviceWorkflow, error } = await supabase
          .from('service_workflows')
          .select('id, name')
          .eq('service_id', service.id)
          .eq('is_active', true)
          .single();

        if (serviceWorkflow && !error) {
          console.log(`Service "${service.name}" has workflow: ${serviceWorkflow.name}`);
          
          // Track that this service instance has a workflow template
          setServicesWithWorkflows(prev => new Set(prev).add(instanceId));
          
          // Pass the service with instanceId info
          setCurrentServiceForWorkflow({ ...service, instanceId });
          setIsSimpleWorkflowModalOpen(true);
          return; // Exit early - workflow modal will handle next steps
        }
      } catch (error) {
        console.log(`Service "${service.name}" has no workflow template`);
        // Continue to addon check
      }
    }
    
    // Check if service has addons before opening the modal
    if (opts?.silent) return;
    try {
      const availableAddons = await getServiceAddons(service.id);
      
      // Only open addons modal if there are actually addons available
      if (availableAddons && availableAddons.length > 0) {
        console.log(`Service "${service.name}" has ${availableAddons.length} addons available - opening addons modal`);
        setCurrentService(service);
        setIsSelectAddonsModalOpen(true);
      } else {
        console.log(`Service "${service.name}" has no addons available - skipping addons modal`);
        // No addons available, just continue without opening the modal
      }
    } catch (error) {
      console.error('Error checking for service addons:', error);
      // On error, don't open the modal to be safe
    }
  };

  const handleAddProduct = async (product: any) => {
    // Check industry - for Home Services, vehicle is optional
    const isHomeServices = selectedBusiness?.industry === 'Home Services';
    
    if (!isHomeServices && !currentVehicle) {
      console.error('No current vehicle selected for product');
      return;
    }

    // Generate unique instance ID for this product instance
    const instanceId = `product_${product.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const newProductSelection: ProductSelection = {
      instanceId,
      vehicle: currentVehicle || null, // Allow null for Home Services
      product: {
        id: product.id,
        name: product.name,
        acquisition_cost: product.acquisition_cost || 0,
        price: product.price || 0,
        quantity_available: product.quantity || 0,
        category_name: product.category_name || 'Uncategorized',
        quantity: 1 // Default quantity for booking
      }
    };

    // Calculate new products array
    const newProductsArray = [...selectedProducts, newProductSelection];
    setSelectedProducts(newProductsArray);
    
    // Load photo for this product if not already loaded
    if (!productPhotos[product.id]) {
      try {
        const photoMap = await ProductPhotoService.getPhotosByStockItemIds([product.id]);
        if (photoMap[product.id]) {
          setProductPhotos(prev => ({
            ...prev,
            [product.id]: photoMap[product.id].photo_url
          }));
        }
      } catch (error) {
        console.error('Error loading product photo:', error);
      }
    }
    
    // Close the modal
    setIsSelectInventoryItemModalOpen(false);
    
    // Save progress with new products array
    saveBookingProgress({ selectedProducts: newProductsArray });
  };

  const handleUpdateProductQuantity = async (product: any, newQuantity: number) => {
    // Check industry - for Home Services, vehicle is optional
    const isHomeServices = selectedBusiness?.industry === 'Home Services';
    
    if (!isHomeServices && !currentVehicle) {
      console.error('No current vehicle selected for product quantity update');
      return;
    }

    if (newQuantity <= 0) {
      // Remove the product if quantity is 0 or less
      const newProductsArray = selectedProducts.filter(p => {
        if (p.product.id !== product.id) return true;
        // For Home Services, match by product only (vehicle can be null)
        if (isHomeServices) return false;
        // For Automotive, match by both product and vehicle
        return p.vehicle?.id !== currentVehicle?.id;
      });
      setSelectedProducts(newProductsArray);
      saveBookingProgress({ selectedProducts: newProductsArray });
      return;
    }

    // Update the quantity of the existing product
    const newProductsArray = selectedProducts.map(p => {
      // For Home Services, match by product only (vehicle can be null)
      const matchesProduct = p.product.id === product.id;
      const matchesVehicle = isHomeServices 
        ? (p.vehicle === null && currentVehicle === null) || (p.vehicle?.id === currentVehicle?.id)
        : p.vehicle?.id === currentVehicle?.id;
      
      if (matchesProduct && matchesVehicle) {
        return {
          ...p,
          product: {
            ...p.product,
            quantity: newQuantity
          }
        };
      }
      return p;
    });

    setSelectedProducts(newProductsArray);
    saveBookingProgress({ selectedProducts: newProductsArray });
  };

  // Component for product icon - shows photo if available, otherwise blue icon
  const ProductIcon: React.FC<{ productId: string; productName: string }> = ({ productId, productName }) => {
    const photoUrl = productPhotos[productId];
    
    if (photoUrl) {
      return (
        <div className="h-4 w-4 rounded overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          <img 
            src={photoUrl} 
            alt={productName}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-600"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg>';
                parent.className = "h-4 w-4 rounded bg-green-100 flex items-center justify-center flex-shrink-0";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-4 w-4 rounded bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
        <Package2 className="h-3 w-3" />
      </div>
    );
  };

  // Simple workflow modal handlers
  const handleWorkflowSkip = () => {
    console.log('User skipped workflow assignment');
    setIsSimpleWorkflowModalOpen(false);
    setCurrentServiceForWorkflow(null);
    
    // Continue to addon check for the current service
    if (currentServiceForWorkflow) {
      checkForServiceAddons(currentServiceForWorkflow);
    }
  };

  const handleWorkflowContinue = (bookingWorkflowSteps: any[]) => {
    console.log('✅ User chose to continue with workflow assignment');
    console.log('📋 Booking workflow steps:', bookingWorkflowSteps);
    
    // Store workflow data for this service
    if (currentServiceForWorkflow && bookingWorkflowSteps && bookingWorkflowSteps.length > 0) {
      // The data is already in the correct format for booking_services.workflow_steps JSONB column
      const workflowData = bookingWorkflowSteps.map((step: any) => ({
        workflow_step_library_id: step.workflow_step_library_id,
        title: step.title,
        description: step.description,
        order_index: step.order_index,
        photos_enabled: step.photos_enabled || false,
        assigned_resources: step.assigned_resources.map((resource: any) => ({
          id: resource.id,
          name: `${resource.first_name} ${resource.last_name}`,
          avatar_url: resource.avatar_url
        }))
      }));

      // Store workflow data temporarily (will be saved when booking is created)
      setSelectedServices(prev => prev.map(service => 
        service.instanceId === currentServiceForWorkflow.instanceId 
          ? { ...service, service: { ...service.service, workflowData } }
          : service
      ));

      // Track that this service instance now has an assigned workflow
      if (currentServiceForWorkflow.instanceId) {
        setServicesWithAssignedWorkflows(prev => new Set(prev).add(currentServiceForWorkflow.instanceId!));
      }

      // Extract all unique resources from workflow steps and add to selectedResources
      const workflowResources = new Map<string, any>();
      
      bookingWorkflowSteps.forEach((step: any) => {
        step.assigned_resources.forEach((resource: any) => {
          if (!workflowResources.has(resource.id)) {
            workflowResources.set(resource.id, {
              id: resource.id,
              first_name: resource.first_name,
              last_name: resource.last_name,
              email: resource.email,
              avatar_url: resource.avatar_url
            });
          }
        });
      });

      // Add workflow resources to selectedResources (avoiding duplicates)
      setSelectedResources(prev => {
        const existingResourceIds = new Set(prev.map(r => r.id));
        const newResources = Array.from(workflowResources.values()).filter(
          resource => !existingResourceIds.has(resource.id)
        );
        
        if (newResources.length > 0) {
          console.log('🔄 Auto-adding workflow resources to booking:', newResources.map(r => `${r.first_name} ${r.last_name}`));
        }
        
        return [...prev, ...newResources];
      });

      console.log('Workflow data stored for service:', currentServiceForWorkflow.name, workflowData);
    }
    
    setIsSimpleWorkflowModalOpen(false);
    setCurrentServiceForWorkflow(null);
    
    // Continue to addon check
    if (currentServiceForWorkflow) {
      checkForServiceAddons(currentServiceForWorkflow);
    }
  };

  const checkForServiceAddons = async (service: ExtendedService) => {
    console.log('🔍 [DEBUG] checkForServiceAddons called with service:', service.name, service.id);
    try {
      const availableAddons = await getServiceAddons(service.id);
      console.log('🔍 [DEBUG] getServiceAddons returned:', availableAddons);
      
      if (availableAddons && availableAddons.length > 0) {
        console.log(`✅ Service "${service.name}" has ${availableAddons.length} addons available`);
        setCurrentService(service);
        setIsSelectAddonsModalOpen(true);
        console.log('🔍 [DEBUG] Modal state set - isSelectAddonsModalOpen: true');
      } else {
        console.log(`ℹ️ Service "${service.name}" has no addons available - opening modal anyway for testing`);
        // Temporarily open modal even if no addons for debugging
        setCurrentService(service);
        setIsSelectAddonsModalOpen(true);
        console.log('🔍 [DEBUG] Modal opened for debugging even with no addons');
      }
    } catch (error) {
      console.error('❌ Error checking for service addons:', error);
      // Open modal anyway to see if there's a different issue
      setCurrentService(service);
      setIsSelectAddonsModalOpen(true);
      console.log('🔍 [DEBUG] Modal opened despite error for debugging');
    }
  };

  const handleAddAddons = (addons: ServiceAddon[]) => {
    if (!currentService) return;
    
    // Calculate new addons object
    const newAddonsObject = {
      ...selectedServiceAddons,
      [currentService.id]: addons
    };
    setSelectedServiceAddons(newAddonsObject);
    
    // Save progress with new addons immediately - bypass timing issues
    saveBookingProgress({ selectedServiceAddons: newAddonsObject });
  };

  // Group services by vehicle
  const servicesByVehicle = selectedServices.reduce<Record<string, typeof selectedServices>>((acc, service) => {
    // Handle null/undefined vehicle IDs properly - use 'no-vehicle' as key instead of null
    const vehicleId = service.vehicle?.id || 'no-vehicle';
    if (!acc[vehicleId]) {
      acc[vehicleId] = [];
    }
    acc[vehicleId].push(service);
    return acc;
  }, {});

  const handleSelectVehicle = (vehicle: CustomerVehicle) => {
    setCurrentVehicle(vehicle);
    setIsSelectServiceModalOpen(true);
    setIsSelectVehicleModalOpen(false);
    
    // Save progress after vehicle selection - pass vehicle directly to avoid timing issues
    saveBookingProgress({ currentVehicle: vehicle });
  };

  // Set booking type when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log("Modal opened, setting initial booking type");
      // This is now a location-only booking modal
      setIsLocationBooking(true);
    }
  }, [isOpen]); // Only run when modal opens

  const handleRemoveService = (instanceId: string) => {
    const removedService = selectedServices.find(s => s.instanceId === instanceId);
    const newServicesArray = selectedServices.filter(s => s.instanceId !== instanceId);
    setSelectedServices(newServicesArray);
    
    if (removedService?.instanceId) {
      setServicesWithWorkflows(prev => {
        const newSet = new Set(prev);
        newSet.delete(removedService.instanceId);
        return newSet;
      });
      setServicesWithAssignedWorkflows(prev => {
        const newSet = new Set(prev);
        newSet.delete(removedService.instanceId);
        return newSet;
      });
    }
    
    saveBookingProgress({ selectedServices: newServicesArray });
  };

  const handleRemoveAddon = (serviceId: string, addonId: string) => {
    // Calculate new addons object
    const newAddonsObject = {
      ...selectedServiceAddons,
      [serviceId]: selectedServiceAddons[serviceId]?.filter(addon => addon.id !== addonId) || []
    };
    setSelectedServiceAddons(newAddonsObject);
    
    // Save progress with new addons immediately - bypass timing issues
    saveBookingProgress({ selectedServiceAddons: newAddonsObject });
  };

  // Enhanced handlers with explicit saves on user actions
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    // Save immediately when title changes (user typed something meaningful)
    if (newTitle.trim().length > 0) {
      setTimeout(() => saveBookingProgress(), 500); // Debounce typing
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCurrentVehicle(null);
    setIsSelectCustomerModalOpen(false);
    setSourceProposal(null);
    
    // Reset referral source when customer changes
    setReferralSourceCustomer(null);
    
    // Auto-detect customer source based on repeat status
    detectCustomerSource(customer);
    
    // Save progress after customer selection - pass customer directly to avoid timing issues
    saveBookingProgress({ selectedCustomer: customer });

    const openResourceIfReady = () => {
      // Customer first: only auto-open resources once schedule is set and none assigned
      if (
        !isUnscheduled &&
        scheduleData.startDate &&
        scheduleData.startTime &&
        selectedResources.length === 0 &&
        !initialResource
      ) {
        setIsSelectResourceModalOpen(true);
      }
    };

    // Background check: open/approved proposals for this customer (skip if already converting a proposal)
    if (!initialProposal && selectedBusiness?.id && customer.id) {
      void (async () => {
        try {
          const openProposals = await getConvertibleProposalsForCustomer(selectedBusiness.id, customer.id);
          if (openProposals.length > 0) {
            setExistingProposals(openProposals);
            setIsExistingProposalsModalOpen(true);
            return;
          }
        } catch (err) {
          console.warn('Could not check customer proposals on booking create:', err);
        }
        openResourceIfReady();
      })();
    } else {
      openResourceIfReady();
    }
  };

  const handleIgnoreExistingProposals = () => {
    setIsExistingProposalsModalOpen(false);
    setExistingProposals([]);
    if (
      !isUnscheduled &&
      scheduleData.startDate &&
      scheduleData.startTime &&
      selectedResources.length === 0 &&
      !initialResource
    ) {
      setIsSelectResourceModalOpen(true);
    }
  };

  const handleConvertExistingProposal = async (summary: ConvertibleProposalSummary) => {
    try {
      setConvertingExistingProposal(true);
      const full = await getProposal(summary.id);
      setSourceProposal(full);
      await loadProposalData(full);
      await loadProposalDeposits(full);
      setIsExistingProposalsModalOpen(false);
      setExistingProposals([]);
    } catch (err) {
      console.error('Error converting existing proposal into booking:', err);
    } finally {
      setConvertingExistingProposal(false);
    }
  };

  const handleScheduleChange = (type: string, date: string, time: string) => {
    // Simple schedule update - no persistence needed for schedule data
    setScheduleData(prev => ({
      ...prev,
      [`${type}Date`]: date,
      [`${type}Time`]: time
    }));
  };

  const handleCustomerNotesChange = (notes: string) => {
    // Simple notes update - no persistence needed for schedule data
    setScheduleData(prev => ({
      ...prev,
      customer_notes: notes
    }));
  };

  // ── AI assistant surface ───────────────────────────────────────────────
  // The assistant's booking tools (set_booking_notes, add_booking_service,
  // create_vehicle...) call this modal through the assistant surface registry
  // (registerAssistantSurface below). Each handler returns a ground-truth
  // result string for the model. The ref is reassigned every render so
  // handlers always see current state.
  // Mirrors the Create Booking button's exact disabled logic so every AI tool
  // result can report whether the form is complete. Overrides let a handler
  // report the state AFTER its own mutation (React state hasn't flushed yet).
  const bookingReadinessLine = (over: Partial<{
    serviceCount: number;
    durationMinutes: number;
    scheduleSet: boolean;
    resourceCount: number;
  }> = {}): string => {
    const serviceCount = over.serviceCount ?? selectedServices.length;
    const durMin = over.durationMinutes ?? totalDurationMinutes;
    const scheduleSet = over.scheduleSet ?? (isUnscheduled || (!!scheduleData.startDate && !!scheduleData.startTime));
    const resourceCount = over.resourceCount ?? selectedResources.length;
    const missing: string[] = [];
    if (!title) missing.push('a title');
    if (!selectedCustomer) missing.push('a customer');
    if (serviceCount === 0) missing.push('at least one service');
    if (!scheduleSet) missing.push('a date & time (the USER must say when — never call set_booking_schedule with a date/time they did not give)');
    if (!isUnscheduled && resourceCount === 0) missing.push('an assigned resource (a team member the USER must name — never call manage_booking_resources with a name they did not say; ask "who\'s doing this job?" at wrap-up)');
    if (!selectedCustomerSource && !isWarrantyMode) missing.push('a customer source');
    if (serviceCount > 0 && durMin < 30) missing.push('at least 30 minutes of total service time');
    return missing.length === 0
      ? ' FORM STATUS: READY — every required field is filled and the Create Booking button is active. Once the user\'s named details are all done, tell them the booking is ready and ask if you should create it. Press Create Booking ONLY after they confirm.'
      : ` FORM STATUS: not ready to create yet — still needs ${missing.join(', ')}. For any missing piece the user hasn't named, ASK them — never pick a value yourself.`;
  };

  const aiBridgeRef = useRef<{
    setNotes: (detail: any) => string;
    addService: (detail: any) => Promise<string>;
    updateService: (detail: any) => string;
    setSchedule: (detail: any) => string;
    createVehicle: (detail: any) => Promise<string>;
    manageResource: (detail: any) => Promise<string>;
    setAutomationsDisabled: (detail: any) => string;
  }>({ setNotes: () => '', addService: async () => '', updateService: () => '', setSchedule: () => '', createVehicle: async () => '', manageResource: async () => '', setAutomationsDisabled: () => '' });

  aiBridgeRef.current = {
    setNotes: (detail) => {
      const notes = String(detail?.notes ?? '').slice(0, 2000);
      handleCustomerNotesChange(notes);
      return `Booking notes set to: "${notes}".` + bookingReadinessLine();
    },

    setAutomationsDisabled: (detail) => {
      const disabled = detail?.disabled === true || detail?.disabled === 'true';
      setRemoveFromAutomations(disabled);
      return (
        `"Remove from automations" is now ${disabled ? 'CHECKED — this booking will not trigger any automations (no confirmation/reminder messages to the customer)' : 'unchecked — automations will run normally for this booking'}.` +
        bookingReadinessLine()
      );
    },

    addService: async (detail) => {
      const service = detail?.service as ExtendedService;
      let priceOption = detail?.priceOption;
      // One-off overrides for THIS booking ("it's 700 this time") — patch the
      // booking's copy of the price option, exactly like EditServiceModal does;
      // the catalog service is untouched.
      const priceOverride = typeof detail?.priceOverride === 'number' ? detail.priceOverride : null;
      const durationOverride = typeof detail?.durationMinutesOverride === 'number' ? detail.durationMinutesOverride : null;
      if (priceOverride !== null || durationOverride !== null) {
        priceOption = {
          ...priceOption,
          ...(priceOverride !== null ? { price: priceOverride } : {}),
          ...(durationOverride !== null ? { duration_minutes: durationOverride } : {}),
        };
      }
      const isHomeServices = selectedBusiness?.industry === 'Home Services';
      let vehicle: CustomerVehicle | null = null;

      if (!isHomeServices) {
        if (!selectedCustomer) {
          return 'error: no customer is selected in the booking form yet — set the customer first (start_booking with customer_id), then add services.';
        }
        const vehicles = selectedCustomer.vehicles || [];
        // Model-only names for the user (a year prefix only when models repeat);
        // database ids are never surfaced.
        const modelNames = vehicles.map((v) =>
          vehicles.some((o) => o !== v && o.model === v.model) ? `${v.year} ${v.model}` : v.model,
        );
        if (vehicles.length === 0) {
          return 'error: this business is automotive — every service needs a vehicle, and this customer has NO vehicles on file. Ask the user for the vehicle (make, model, year), use create_vehicle, then add the service again.';
        }
        const vehicleId = String(detail?.vehicleId || '').trim();
        const vehicleQuery = String(detail?.vehicleQuery || '').trim();
        if (vehicleId && vehicles.some((v) => v.id === vehicleId)) {
          vehicle = vehicles.find((v) => v.id === vehicleId) || null;
        } else if (vehicleQuery) {
          if (/^(any|first|whatever|any\s*one|doesn'?t\s*matter|no\s*preference)$/i.test(vehicleQuery)) {
            vehicle = vehicles[0];
          } else {
            const tokens = vehicleQuery.toLowerCase().split(/\s+/).filter(Boolean);
            const matches = vehicles.filter((v) => {
              const hay = `${v.year} ${v.make} ${v.model} ${v.color || ''} ${v.license_plate || ''}`.toLowerCase();
              return tokens.every((t) => hay.includes(t));
            });
            if (matches.length > 0) {
              vehicle = matches[0];
            } else {
              return `error: none of the customer's vehicles match "${vehicleQuery}". Their vehicles: ${modelNames.join(', ')}. Refer to them by model only (never ids); pass vehicle with one of these, "any" if the user doesn't care, or create_vehicle if it's genuinely not on file.`;
            }
          }
        } else {
          vehicle = vehicles.find((v) => v.is_primary) || vehicles[0];
        }
        setCurrentVehicle(vehicle);
      }

      await handleAddService(service, priceOption, { vehicle, silent: true });
      const price = typeof priceOption?.price === 'number' ? ` $${priceOption.price.toFixed(2)}` : '';
      return (
        `Added "${service.name}" (${priceOption?.name || 'standard'}${price ? `,${price}` : ''})${priceOverride !== null ? ' — custom price for this booking only' : ''}${vehicle ? ` for the ${vehicle.model}` : ''} to the booking.`
        + bookingReadinessLine({
          serviceCount: selectedServices.length + 1,
          durationMinutes: totalDurationMinutes + (Number(priceOption?.duration_minutes) || 0),
        })
      );
    },

    updateService: (detail) => {
      if (selectedServices.length === 0) {
        return 'error: no services have been added to this booking yet — add one first with add_booking_service.';
      }
      const describeSel = (s: (typeof selectedServices)[number]) =>
        `"${s.service.name}" ($${Number(s.priceOption?.price ?? 0).toFixed(2)}${s.vehicle ? `, ${s.vehicle.year} ${s.vehicle.make} ${s.vehicle.model}` : ''})`;

      const query = String(detail?.serviceQuery || '').toLowerCase().trim();
      let target = null as (typeof selectedServices)[number] | null;
      if (!query) {
        if (selectedServices.length === 1) target = selectedServices[0];
        else {
          return `error: this booking has ${selectedServices.length} services — say which one to change: ${selectedServices.map(describeSel).join('; ')}.`;
        }
      } else {
        const matches = selectedServices.filter((s) => {
          const name = (s.service.name || '').toLowerCase();
          return name.includes(query) || query.includes(name);
        });
        if (matches.length === 1) target = matches[0];
        else if (matches.length === 0) {
          return `error: no added service matches "${detail?.serviceQuery}". Services on this booking: ${selectedServices.map(describeSel).join('; ')}.`;
        } else {
          return `error: ${matches.length} added services match "${detail?.serviceQuery}": ${matches.map(describeSel).join('; ')}. Ask the user which one.`;
        }
      }

      // Same per-booking patch EditServiceModal applies on save: the
      // selection's copy of the service + price option, never the catalog.
      const newName = detail?.newName ? String(detail.newName) : null;
      const newDescription = detail?.newDescription ? String(detail.newDescription) : null;
      const newPrice = typeof detail?.newPrice === 'number' ? detail.newPrice : null;
      const newDurationMinutes = typeof detail?.newDurationMinutes === 'number' ? detail.newDurationMinutes : null;
      setSelectedServices((prev) =>
        prev.map((s) =>
          s === target
            ? {
                ...s,
                service: {
                  ...s.service,
                  ...(newName ? { name: newName } : {}),
                  ...(newDescription ? { description: newDescription } : {}),
                },
                priceOption: {
                  ...s.priceOption,
                  ...(newPrice !== null ? { price: newPrice } : {}),
                  ...(newDurationMinutes !== null ? { duration_minutes: newDurationMinutes } : {}),
                },
              }
            : s,
        ),
      );
      const changes = [
        newName ? `title → "${newName}"` : null,
        newDescription ? 'description updated' : null,
        newPrice !== null ? `price → $${newPrice.toFixed(2)}` : null,
        newDurationMinutes !== null ? `duration → ${(newDurationMinutes / 60).toFixed(newDurationMinutes % 60 === 0 ? 0 : 1)}h` : null,
      ].filter(Boolean).join(', ');
      return (
        `Updated "${target.service.name}" on this booking (${changes}) — the catalog service is unchanged.`
        + bookingReadinessLine(
          newDurationMinutes !== null
            ? { durationMinutes: totalDurationMinutes - (Number(target.priceOption?.duration_minutes) || 0) + newDurationMinutes }
            : {},
        )
      );
    },

    setSchedule: (detail) => {
      const startDate = String(detail?.startDate || '');
      const startTime = String(detail?.startTime || '');
      const dropOffDate = String(detail?.dropOffDate || startDate);
      const dropOffTime = String(detail?.dropOffTime || startTime);
      const start = new Date(`${startDate}T${startTime}`);
      const dropOff = new Date(`${dropOffDate}T${dropOffTime}`);
      if (isNaN(start.getTime()) || isNaN(dropOff.getTime())) {
        return 'error: invalid date/time — use date YYYY-MM-DD and time HH:MM (24h).';
      }
      const durMin = totalDurationMinutes > 0 ? totalDurationMinutes : 60;
      let endDate: string;
      let endTime: string;
      let endNote: string;
      const explicitEndDate = String(detail?.endDate || '');
      const explicitEndTime = String(detail?.endTime || '');
      if (explicitEndDate) {
        // Caller gave an explicit end — honor it as-is
        const explicitEnd = new Date(`${explicitEndDate}T${explicitEndTime || startTime}`);
        if (isNaN(explicitEnd.getTime()) || explicitEnd.getTime() <= start.getTime()) {
          return 'error: end date/time must be valid and after the start.';
        }
        endDate = format(explicitEnd, 'yyyy-MM-dd');
        endTime = format(explicitEnd, 'HH:mm');
        endNote = 'explicit end from the user';
      } else if (selectedBusiness?.business_hours) {
        // Stretch the duration across the business's working hours, exactly
        // like the schedule editor (closed days skipped, open→close each day)
        const stretched = calculateEndAcrossBusinessHours(selectedBusiness.business_hours, startDate, startTime, durMin / 60);
        endDate = stretched.date;
        endTime = stretched.time;
        endNote = endDate !== startDate
          ? 'stretched across the business\'s working hours over multiple days'
          : 'within business hours';
      } else {
        const end = new Date(start.getTime() + durMin * 60_000);
        endDate = format(end, 'yyyy-MM-dd');
        endTime = format(end, 'HH:mm');
        endNote = 'no business hours configured — straight duration';
      }
      const end = new Date(`${endDate}T${endTime}`);
      setIsUnscheduled(false);
      setScheduleData(prev => ({
        ...prev,
        dropOffDate,
        dropOffTime,
        startDate,
        startTime,
        endDate,
        endTime,
      }));
      const pastNote = start.getTime() < Date.now()
        ? ' WARNING: that start is in the PAST — confirm the date with the user.'
        : '';
      return (
        `Schedule set — drop-off ${format(dropOff, 'EEE, MMM d, h:mm a')}, start ${format(start, 'EEE, MMM d, h:mm a')}, end ${format(end, 'EEE, MMM d, h:mm a')} (${durMin} min${totalDurationMinutes > 0 ? ' from the added services' : ' default — no services added yet'}; ${endNote}).${pastNote}`
        + bookingReadinessLine({ scheduleSet: true })
      );
    },

    createVehicle: async (detail) => {
      if (selectedBusiness?.industry === 'Home Services') {
        return 'This business is Home Services — bookings never involve vehicles. Skip the vehicle entirely and continue with the rest of the request.';
      }
      if (!selectedCustomer?.id) {
        return 'error: no customer is selected in the booking form yet.';
      }
      try {
        const newVehicle = await createCustomerVehicle({
          customer_id: selectedCustomer.id,
          make: String(detail?.make || '').trim(),
          model: String(detail?.model || '').trim(),
          year: Number(detail?.year) || new Date().getFullYear(),
          vin: null,
          color: detail?.color ? String(detail.color) : null,
          license_plate: null,
          mileage: null,
          notes: null,
          is_primary: (selectedCustomer.vehicles || []).length === 0,
          last_service: null,
        });
        const updated = await getCustomer(selectedCustomer.id);
        setSelectedCustomer(updated);
        return `Created vehicle ${newVehicle.year} ${newVehicle.make} ${newVehicle.model} on this customer. Now add the service with vehicle: "${newVehicle.model}".`;
      } catch (err) {
        return `error: could not create the vehicle — ${err instanceof Error ? err.message : 'unknown error'}.`;
      }
    },

    manageResource: async (detail) => {
      const action = detail?.action === 'remove' ? 'remove' : 'add';
      const query = String(detail?.query || '').trim().toLowerCase();
      if (!query) {
        return 'error: the resource\'s name is required.';
      }
      const nameOf = (r: any) => (`${r.first_name || ''} ${r.last_name || ''}`.trim() || r.name || 'Unnamed');
      const listNames = (list: any[]) => (list.length ? list.map(nameOf).join(', ') : 'none');
      const tokens = query.split(/\s+/).filter(Boolean);
      const matches = (r: any) => {
        const hay = nameOf(r).toLowerCase();
        return tokens.every((t) => hay.includes(t));
      };

      if (action === 'remove') {
        const found = selectedResources.filter(matches);
        if (found.length === 0) {
          return `error: no assigned resource matches "${query}". Currently assigned: ${listNames(selectedResources)}.`;
        }
        if (found.length > 1) {
          return `error: ${found.length} assigned resources match "${query}": ${listNames(found)}. Ask the user which one.`;
        }
        handleResourceRemove(found[0].id);
        return (
          `Removed ${nameOf(found[0])} from the booking. Now assigned: ${listNames(selectedResources.filter((r) => r.id !== found[0].id))}.`
          + bookingReadinessLine({ resourceCount: selectedResources.length - 1 })
        );
      }

      if (!selectedBusiness?.id) {
        return 'error: no business selected.';
      }
      try {
        // Same roster the Assign Resources modal shows: active, bookable team
        const all = await getResources(selectedBusiness.id);
        const bookable = (all || []).filter((r: any) => r.status === 'active' && !r.not_bookable);
        const found = bookable.filter(matches);
        if (found.length === 0) {
          return `error: no team member matches "${query}". Available: ${listNames(bookable)}.`;
        }
        if (found.length > 1) {
          return `error: ${found.length} team members match "${query}": ${listNames(found)}. Ask the user which one.`;
        }
        if (selectedResources.some((r) => r.id === found[0].id)) {
          return `${nameOf(found[0])} is already assigned to this booking. Assigned: ${listNames(selectedResources)}.`;
        }
        handleResourceAdd(found[0]);
        return (
          `Added ${nameOf(found[0])} to the booking. Now assigned: ${listNames([...selectedResources, found[0]])}.`
          + bookingReadinessLine({ resourceCount: selectedResources.length + 1 })
        );
      } catch (err) {
        return `error: could not load the team list — ${err instanceof Error ? err.message : 'unknown error'}.`;
      }
    },
  };

  // Register this modal as the assistant's "booking" surface while open.
  // Handlers delegate through the ref so they always see current state.
  useEffect(() => {
    if (!isOpen) return;
    return registerAssistantSurface('booking', {
      setNotes: (payload) => aiBridgeRef.current.setNotes(payload),
      addService: (payload) => aiBridgeRef.current.addService(payload),
      updateService: (payload) => aiBridgeRef.current.updateService(payload),
      setSchedule: (payload) => aiBridgeRef.current.setSchedule(payload),
      createVehicle: (payload) => aiBridgeRef.current.createVehicle(payload),
      manageResource: (payload) => aiBridgeRef.current.manageResource(payload),
      setAutomationsDisabled: (payload) => aiBridgeRef.current.setAutomationsDisabled(payload),
      cancel: () => {
        onClose();
        return 'Cancelled — the booking form is closed and nothing was saved.';
      },
    });
  }, [isOpen]);

  const handleColorChange = (color: string) => {
    setBookingColor(color);
    
    // Save progress with new color immediately
    saveBookingProgress({ bookingColor: color });
  };

  const handleResourceAdd = (resource: any) => {
    // Check if resource is already selected
    if (!selectedResources.some(r => r.id === resource.id)) {
      // Calculate new resources array
      const newResourcesArray = [...selectedResources, resource];
      setSelectedResources(newResourcesArray);
      
      // Save progress with new resources immediately - bypass timing issues
      saveBookingProgress({ selectedResources: newResourcesArray });
    }
    setIsSelectResourceModalOpen(false);
  };

  const handleResourceRemove = (resourceId: string) => {
    // Simple resource removal - workflow protection will be rebuilt
    const newResourcesArray = selectedResources.filter(r => r.id !== resourceId);
    setSelectedResources(newResourcesArray);
    
    // Save progress with new resources immediately - bypass timing issues
    saveBookingProgress({ selectedResources: newResourcesArray });
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // This is now a location-only booking modal
  // Structure matches HelpModal: no padding on overlay; panel positioned with safe-area top/bottom so it fills the screen
  // Portal to body so z-index is not trapped by control-centre panel stacking (relative z-0 ancestors).
  return createPortal(
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{ 
        zIndex: 100200,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100210,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0 }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Create Booking
            </h2>
          </div>

          {/* Content */}
          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: 0
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`} style={{
              paddingBottom: '1rem'
            }}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Title Section — collapsible */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <div
                  className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-gray-50/50 transition-colors active:scale-[0.99] touch-manipulation"
                  onClick={() => setIsTitleExpanded(!isTitleExpanded)}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isTitleExpanded ? '' : '-rotate-90'}`} />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0">Booking Title</span>
                  {!isTitleExpanded && (
                    <span className="text-sm font-medium text-gray-900 truncate">{title || 'Booking'}</span>
                  )}
                </div>
                {isTitleExpanded && (
                  <div className="px-3 pb-3">
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      placeholder="Enter booking title..."
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                )}
              </div>

               {/* Original Booking Number Section - Only for Warranty Bookings */}
               {isWarrantyMode && (
                 <div className="bg-white rounded-xl border border-blue-200 overflow-hidden">
                   <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
                     <h3 className="text-base font-semibold text-gray-900 flex items-center">
                       <Shield className="h-4 w-4 mr-2 text-blue-600" />
                       Original Booking
                     </h3>
                   </div>
                   <div className="p-4">
                     <p className="text-sm text-gray-800">
                       Booking #{originalBookingNumber}
                     </p>
                   </div>
                 </div>
               )}



              {/* Customer Selection Section */}
              <div className={`transition-opacity duration-300 ${title ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                {selectedCustomer ? (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                    {/* Collapsible header — always visible */}
                    <div
                      className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-gray-50/50 transition-colors active:scale-[0.99] touch-manipulation"
                      onClick={() => setIsCustomerSectionExpanded(!isCustomerSectionExpanded)}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isCustomerSectionExpanded ? '' : '-rotate-90'}`} />
                      <div className="h-5 w-5 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm flex items-center justify-center text-white font-bold text-[9px] flex-shrink-0 uppercase">
                        {getPersonInitials(selectedCustomer.first_name, selectedCustomer.last_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-gray-900 truncate block">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </span>
                        {!isCustomerSectionExpanded && (
                          <span className="text-[11px] truncate block flex items-center gap-1">
                            <span className={selectedCustomerSource ? 'text-gray-400' : 'text-red-500 font-semibold'}>
                              {selectedCustomerSource ? selectedCustomerSource.name : 'No Source'}
                            </span>
                            {selectedCustomer.email && (
                              <span className="text-gray-300">· {selectedCustomer.email}</span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expanded body */}
                    {isCustomerSectionExpanded && (
                    <div className="space-y-4 px-3 pb-3">
                      {/* Customer Card */}
                      <div 
                        className="bg-gray-50 rounded-xl p-2.5 relative transition-all duration-150 cursor-pointer active:scale-[0.98] touch-manipulation"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsEditCustomerModalOpen(true);
                        }}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="flex items-center space-x-2.5 mb-2">
                          <div className="h-6 w-6 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0 uppercase">
                            {getPersonInitials(selectedCustomer.first_name, selectedCustomer.last_name)}
                          </div>
                          <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight flex-1 min-w-0">
                            {selectedCustomer.first_name} {selectedCustomer.last_name}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsSelectCustomerModalOpen(true);
                            }}
                            className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation flex-shrink-0"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Change
                          </button>
                        </div>
                        <div className="border-t border-gray-200/60 my-2"></div>
                        <div className="space-y-1.5 pt-1">
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Mail className="h-3.5 w-3.5 text-gray-400" />
                            <span className="truncate">{selectedCustomer.email}</span>
                          </div>
                          {selectedCustomer.phone && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <Phone className="h-3.5 w-3.5 text-gray-400" />
                              <span>{selectedCustomer.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>

                    {/* Customer Source - Only show when customer is selected */}
                    {!isWarrantyMode && (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Customer Source
                        </label>
                        <div className="relative">
                          <button
                            ref={customerSourceButtonRef}
                            type="button"
                            data-assistant-customer-source={selectedCustomerSource?.name || ''}
                            onClick={() => setIsCustomerSourceDropdownOpen(!isCustomerSourceDropdownOpen)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-sm bg-white rounded-xl hover:bg-gray-50 focus:outline-none transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                              selectedCustomerSource 
                                ? 'border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                                : 'border-2 border-red-300 bg-red-50 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                            }`}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <span className={selectedCustomerSource ? 'text-gray-700' : 'text-red-600'}>
                              {selectedCustomerSource ? selectedCustomerSource.name : 'Select source...'}
                            </span>
                            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${
                              isCustomerSourceDropdownOpen ? 'rotate-180' : ''
                            } ${selectedCustomerSource ? 'text-gray-400' : 'text-red-400'}`} />
                          </button>
                          
                          {/* Customer Source Dropdown Portal */}
                          {isCustomerSourceDropdownOpen && customerSourceButtonRef.current && createPortal(
                            <>
                              <div 
                                className="fixed inset-0" 
                                style={{ zIndex: 100220 }}
                                onClick={() => setIsCustomerSourceDropdownOpen(false)}
                              />
                              <div 
                                className="fixed bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                                style={{ 
                                  zIndex: 100230,
                                  left: customerSourceButtonRef.current.getBoundingClientRect().left,
                                  top: customerSourceButtonRef.current.getBoundingClientRect().bottom + 4,
                                  width: customerSourceButtonRef.current.getBoundingClientRect().width,
                                  maxWidth: '400px'
                                }}
                              >
                                {selectedCustomerSource ? (
                                  <div 
                                    onClick={() => {
                                      setSelectedCustomerSource(null);
                                      setIsCustomerSourceDropdownOpen(false);
                                    }}
                                    className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors"
                                  >
                                    Clear source
                                  </div>
                                ) : (
                                  <div className="px-3 py-2 text-sm text-gray-400">
                                    No source selected
                                  </div>
                                )}
                                
                                {/* Regular Customer Sources (includes system defaults like Repeat Customer) */}
                                {(() => {
                                  console.log('🎨 [CreateBookingModal] Rendering customer sources dropdown:', {
                                    customerSourcesLength: customerSources.length,
                                    customerSources: customerSources.map(s => ({ id: s.id, name: s.name }))
                                  });
                                  // Filter out "Warranty" sources for regular bookings
                                  const filteredSources = customerSources.filter(source => source.name.toLowerCase() !== 'warranty');
                                  return filteredSources.map((source) => {
                                    const isSystemRepeat =
                                      source.id === 'repeat-customer' ||
                                      source.name.toLowerCase() === 'repeat customer';
                                    const isSelected =
                                      selectedCustomerSource?.id === source.id ||
                                      (!!selectedCustomerSource &&
                                        selectedCustomerSource.name.toLowerCase() === source.name.toLowerCase());
                                    return (
                                    <div
                                      key={source.id}
                                      onClick={async () => {
                                        console.log('📱 [CreateBookingModal] Customer source selected:', source);
                                        setSelectedCustomerSource(source);
                                        setIsCustomerSourceDropdownOpen(false);
                                        // Reset referral source when source changes (unless it's referral)
                                        if (source.name.toLowerCase() !== 'referral') {
                                          setReferralSourceCustomer(null);
                                        } else {
                                          // If referral is selected, reload referral program status to ensure it's up to date
                                          await loadReferralProgramStatus();
                                        }
                                      }}
                                      className={`px-3 py-2 text-sm cursor-pointer transition-colors border-t border-gray-200 flex items-center justify-between ${
                                        isSelected
                                          ? 'bg-blue-50 text-blue-900 font-medium'
                                          : 'text-gray-900 hover:bg-gray-50'
                                      }`}
                                    >
                                      <span>{source.name}</span>
                                      {isSystemRepeat ? (
                                        <span className="text-xs text-green-600 font-medium">System</span>
                                      ) : null}
                                    </div>
                                    );
                                  });
                                })()}
                                
                                {/* Add Source Option */}
                                <div
                                  onClick={() => {
                                    setIsCustomerSourceDropdownOpen(false);
                                    setIsCreateSourceModalOpen(true);
                                  }}
                                  className="px-3 py-2 text-sm text-blue-600 hover:bg-gray-50 cursor-pointer transition-colors border-t border-gray-200 flex items-center space-x-2"
                                >
                                  <Plus className="h-4 w-4" />
                                  <span>Add Source</span>
                                </div>
                              </div>
                            </>,
                            document.body
                          )}
                        </div>
                        
                        {/* Referral Source - Only show when: new customer + source=referral + program active */}
                        {(() => {
                          const hasCustomer = !!selectedCustomer;
                          const isNewCustomer = selectedCustomer?.repeat === false;
                          const sourceIsReferral = selectedCustomerSource?.name?.toLowerCase() === 'referral';
                          const shouldShow = hasCustomer && isNewCustomer && sourceIsReferral && referralProgramActive;
                          
                          if (sourceIsReferral) {
                            console.log('🔍 Referral source field visibility check:', {
                              hasCustomer,
                              isNewCustomer,
                              customerRepeat: selectedCustomer?.repeat,
                              sourceIsReferral,
                              sourceName: selectedCustomerSource?.name,
                              programActive: referralProgramActive,
                              shouldShow
                            });
                          }
                          
                          return shouldShow;
                        })() && (
                          <div className="mt-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Referral Source
                            </label>
                            <button
                              type="button"
                              onClick={() => setIsSelectReferralSourceModalOpen(true)}
                              className={`w-full flex items-center justify-between px-4 py-3 text-sm bg-white rounded-xl hover:bg-gray-50 focus:outline-none transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                                referralSourceCustomer 
                                  ? 'border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                                  : 'border-2 border-dashed border-blue-300 bg-blue-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                              }`}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <span className={referralSourceCustomer ? 'text-gray-700' : 'text-blue-600 font-medium'}>
                                {referralSourceCustomer 
                                  ? `${referralSourceCustomer.first_name} ${referralSourceCustomer.last_name}`
                                  : 'Select referring customer...'}
                              </span>
                              <Users className={`h-4 w-4 ${referralSourceCustomer ? 'text-gray-400' : 'text-blue-400'}`} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                      {/* Booking Notes */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                          Booking Notes
                        </label>
                        <textarea
                          value={scheduleData.customer_notes || ''}
                          onChange={(e) => handleCustomerNotesChange(e.target.value)}
                          placeholder="Add booking notes..."
                          className="w-full h-24 px-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                        />
                      </div>
                    </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsSelectCustomerModalOpen(true);
                      }}
                      className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Select Customer
                    </button>
                  </div>
                )}
              </div>

              {/* Services Section — collapsible */}
              {selectedCustomer && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <div
                    className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-gray-50/50 transition-colors active:scale-[0.99] touch-manipulation"
                    onClick={() => setIsServicesSectionExpanded(!isServicesSectionExpanded)}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isServicesSectionExpanded ? '' : '-rotate-90'}`} />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0">Services</span>
                    {!isServicesSectionExpanded && selectedServices.length > 0 && (
                      <span className="text-sm text-gray-700 truncate">
                        {selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''} — {selectedServices.map(s => s.service.name).join(', ')}
                      </span>
                    )}
                    {!isServicesSectionExpanded && selectedServices.length === 0 && (
                      <span className="text-sm text-red-500 font-medium">No services added</span>
                    )}
                  </div>
                  {isServicesSectionExpanded && (
                <div className="px-3 pb-3 space-y-6">
                  {Object.entries(servicesByVehicle).map(([vehicleId, services]) => {
                    const vehicle = services[0].vehicle;
                    const isHomeServices = selectedBusiness?.industry === 'Home Services';
                    const isNoVehicle = vehicleId === 'no-vehicle' || !vehicle;
                    
                    return (
                      <div key={vehicleId} className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <div className="p-4">
                          {/* Vehicle Header */}
                          {!isNoVehicle && (
                            <div className="flex items-center justify-between p-4 bg-gray-900 rounded-xl mb-4">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <Car className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <span className="text-base font-semibold text-white truncate">
                                  {vehicle.year} {vehicle.make} {vehicle.model}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCurrentVehicle(vehicle);
                                    setIsSelectVehicleModalOpen(true);
                                  }}
                                  className="px-2.5 py-1 text-xs font-semibold text-blue-400 hover:text-blue-300 bg-white/10 hover:bg-white/15 rounded-lg transition-colors touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  Change
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedVehicleToEdit(vehicle);
                                    setIsEditVehicleModalOpen(true);
                                  }}
                                  className="px-2.5 py-1 text-xs font-semibold text-gray-400 hover:text-gray-300 bg-white/10 hover:bg-white/15 rounded-lg transition-colors touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {/* Automotive only: services attached without a vehicle */}
                          {isNoVehicle && !isHomeServices && (
                            <div className="flex items-center p-4 bg-blue-50 rounded-xl border border-blue-200 mb-4">
                              <div>
                                <div className="text-base font-semibold text-blue-900">Services</div>
                                <div className="text-sm text-blue-700 mt-0.5">No vehicle required</div>
                              </div>
                            </div>
                          )}

                            {/* Services List */}
                            <div className="mt-4 space-y-3">
                              {services.map((item) => {
                                const isDescExpanded = expandedServiceDescriptions.has(item.instanceId);
                                const hasAddonsForService = serviceHasAddons[item.service.id] ?? (selectedServiceAddons[item.service.id]?.length > 0);
                                return (
                                <div key={item.instanceId} className="rounded-xl border border-gray-100 bg-gray-50/50 overflow-hidden">
                                  {/* Service header row */}
                                  <div className="px-3 py-2.5">
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedServiceDescriptions(prev => {
                                          const next = new Set(prev);
                                          next.has(item.instanceId) ? next.delete(item.instanceId) : next.add(item.instanceId);
                                          return next;
                                        })}
                                        className="flex-shrink-0 p-0.5"
                                      >
                                        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${isDescExpanded ? '' : '-rotate-90'}`} />
                                      </button>
                                      <input
                                        type="text"
                                        value={item.service.name}
                                        onChange={(e) => {
                                          setSelectedServices(prev => prev.map(s =>
                                            s.instanceId === item.instanceId ? { ...s, service: { ...s.service, name: e.target.value } } : s
                                          ));
                                        }}
                                        className="text-sm font-semibold text-gray-900 flex-1 min-w-0 bg-transparent border-none focus:outline-none focus:ring-0 px-0 py-0 truncate"
                                        style={{ fontSize: '14px' }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveService(item.instanceId)}
                                        className="flex-shrink-0 p-1 rounded-lg hover:bg-red-50 transition-colors"
                                      >
                                        <X className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
                                      </button>
                                    </div>

                                    {/* Inline price + duration — per-unit/sqft variant */}
                                    {(item.priceOption.pricing_type === 'per_unit' || item.priceOption.pricing_type === 'per_sqft') ? (
                                      <div className="mt-2 space-y-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                            {item.priceOption.pricing_type === 'per_sqft' ? 'PER SQ FT' : 'PER UNIT'}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            ${(item.priceOption.unit_price || 0).toFixed(2)}/{item.priceOption.pricing_type === 'per_sqft' ? 'sqft' : 'unit'} · {item.priceOption.duration_per_unit || 0} hrs/{item.priceOption.pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-[11px] font-medium text-gray-500">{item.priceOption.pricing_type === 'per_sqft' ? 'Sq Ft' : 'Units'}</span>
                                            <input
                                              type="number"
                                              value={item.priceOption.quantity || ''}
                                              placeholder="0"
                                              onChange={(e) => {
                                                const qty = parseFloat(e.target.value) || 0;
                                                const computedPrice = Math.round(qty * (item.priceOption.unit_price || 0) * 100) / 100;
                                                const updates: any = { quantity: qty, price: computedPrice };
                                                if (!(item.priceOption as any)._durationOverridden) {
                                                  updates.duration_minutes = Math.round(qty * (item.priceOption.duration_per_unit || 0) * 60);
                                                }
                                                setSelectedServices(prev => prev.map(s =>
                                                  s.instanceId === item.instanceId ? { ...s, priceOption: { ...s.priceOption, ...updates } } : s
                                                ));
                                              }}
                                              className="w-24 px-2 py-1 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              style={{ fontSize: '14px' }}
                                              min="0"
                                            />
                                          </div>
                                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                            <span>=</span>
                                            <span className="font-semibold text-gray-900">${(item.priceOption.price || 0).toFixed(2)}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Clock className="h-3 w-3 text-gray-400" />
                                            <input
                                              type="number"
                                              value={parseFloat(((item.priceOption.duration_minutes || 0) / 60).toFixed(2))}
                                              onChange={(e) => {
                                                const hours = parseFloat(e.target.value) || 0;
                                                const newDuration = Math.round(hours * 60);
                                                setSelectedServices(prev => prev.map(s =>
                                                  s.instanceId === item.instanceId ? { ...s, priceOption: { ...s.priceOption, duration_minutes: newDuration, _durationOverridden: true } as any } : s
                                                ));
                                              }}
                                              className="w-16 px-1.5 py-0.5 text-xs font-medium text-gray-900 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              step="0.25"
                                              min="0"
                                            />
                                            <span className="text-xs text-gray-400">hrs</span>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                    /* Inline price + duration — flat pricing variant */
                                    <div className="flex items-center gap-3 mt-2">
                                      <div className="flex items-center gap-1.5">
                                        <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                                        <input
                                          type="number"
                                          value={item.priceOption.price}
                                          onChange={(e) => {
                                            const newPrice = parseFloat(e.target.value) || 0;
                                            setSelectedServices(prev => prev.map(s =>
                                              s.instanceId === item.instanceId ? { ...s, priceOption: { ...s.priceOption, price: newPrice } } : s
                                            ));
                                          }}
                                          className="w-20 px-2 py-1 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          style={{ fontSize: '14px' }}
                                          step="0.01"
                                          min="0"
                                        />
                                      </div>
                                      <div className="flex items-center gap-1.5">
                                        <Clock className="h-3.5 w-3.5 text-gray-400" />
                                        <input
                                          type="number"
                                          value={parseFloat(((item.priceOption.duration_minutes || 0) / 60).toFixed(2))}
                                          onChange={(e) => {
                                            const hours = parseFloat(e.target.value) || 0;
                                            const newDuration = Math.round(hours * 60);
                                            setSelectedServices(prev => prev.map(s =>
                                              s.instanceId === item.instanceId ? { ...s, priceOption: { ...s.priceOption, duration_minutes: newDuration } } : s
                                            ));
                                          }}
                                          className="w-16 px-2 py-1 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          style={{ fontSize: '14px' }}
                                          step="0.25"
                                          min="0"
                                        />
                                        <span className="text-xs text-gray-400">hrs</span>
                                      </div>
                                    </div>
                                    )}

                                    {/* Action buttons row */}
                                    <div className="flex items-center gap-2 mt-2">
                                      {hasAddonsForService && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (!item.service?.id) return;
                                            setCurrentService(item.service);
                                            setIsSelectAddonsModalOpen(true);
                                          }}
                                          className="px-2.5 py-1 text-[11px] font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                                        >
                                          Add-ons{selectedServiceAddons[item.service.id]?.length ? ` (${selectedServiceAddons[item.service.id].length})` : ''}
                                        </button>
                                      )}
                                      {workflowsEnabled && servicesWithWorkflows.has(item.instanceId) && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCurrentServiceForWorkflow(item.service);
                                            setIsSimpleWorkflowModalOpen(true);
                                          }}
                                          className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
                                            item.service.workflowData && item.service.workflowData.length > 0
                                              ? 'text-green-700 bg-green-50 hover:bg-green-100'
                                              : 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                                          }`}
                                        >
                                          {item.service.workflowData && item.service.workflowData.length > 0 
                                            ? 'Workflow' 
                                            : 'Assign Workflow'
                                          }
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {/* Expandable description */}
                                  {isDescExpanded && (
                                    <div className="px-3 pb-3 pt-0">
                                      <div className="relative">
                                        <textarea
                                          id={`desc-textarea-${item.instanceId}`}
                                          value={item.service.description || ''}
                                          onChange={(e) => {
                                            setSelectedServices(prev => prev.map(s =>
                                              s.instanceId === item.instanceId ? { ...s, service: { ...s.service, description: e.target.value } } : s
                                            ));
                                          }}
                                          placeholder="Add service description..."
                                          className="w-full px-3 py-2 pb-5 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
                                          rows={3}
                                          style={{ fontSize: '13px', minHeight: '60px' }}
                                        />
                                        <div
                                          className="absolute bottom-0 right-0 w-8 h-8 flex items-center justify-center cursor-s-resize touch-none text-gray-400 active:text-gray-600"
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            const textarea = document.getElementById(`desc-textarea-${item.instanceId}`) as HTMLTextAreaElement;
                                            if (!textarea) return;
                                            const startY = e.clientY;
                                            const startH = textarea.offsetHeight;
                                            const onMove = (ev: MouseEvent) => {
                                              const dy = ev.clientY - startY;
                                              textarea.style.height = Math.max(60, startH + dy) + 'px';
                                            };
                                            const onUp = () => {
                                              document.removeEventListener('mousemove', onMove);
                                              document.removeEventListener('mouseup', onUp);
                                            };
                                            document.addEventListener('mousemove', onMove);
                                            document.addEventListener('mouseup', onUp);
                                          }}
                                        >
                                          <svg width="10" height="10" viewBox="0 0 10 10">
                                            <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                            <line x1="9" y1="5" x2="5" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Service Add-ons */}
                                  {selectedServiceAddons[item.service.id]?.length > 0 && (
                                    <div className="px-3 pb-2.5 space-y-1">
                                      {selectedServiceAddons[item.service.id].map(addon => (
                                        <div key={addon.id} className="flex items-center justify-between py-1 px-2.5 bg-green-50/60 rounded-lg">
                                          <span className="text-xs text-gray-700">{addon.name}</span>
                                          <span className="text-xs font-medium text-gray-500">${addon.price.toFixed(2)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                );
                              })}

                              {/* Products for this vehicle */}
                              {selectedProducts.filter(p => vehicle && p.vehicle.id === vehicle.id).map((productSelection) => (
                                <div key={productSelection.instanceId} className="border-b border-gray-100 pb-4 last:border-b-0">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2">
                                        <ProductIcon productId={productSelection.product.id} productName={productSelection.product.name} />
                                        <div className="text-base font-medium text-gray-900">
                                          {productSelection.product.name}
                                        </div>
                                      </div>
                                      <div className="text-sm text-gray-600 mt-1">{productSelection.product.category_name}</div>
                                      <div className="mt-2 flex items-center space-x-3">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newProducts = selectedProducts.filter(p => p.instanceId !== productSelection.instanceId);
                                            setSelectedProducts(newProducts);
                                            saveBookingProgress({ selectedProducts: newProducts });
                                          }}
                                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-lg font-semibold text-green-600">
                                        ${(productSelection.product.price * productSelection.product.quantity).toFixed(2)}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        Qty: {productSelection.product.quantity}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Add Service Button */}
                            <div className="mt-6">
                              <button
                                type="button"
                                onClick={() => {
                                  setCurrentVehicle(vehicle);
                                  setIsSelectServiceModalOpen(true);
                                }}
                                className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Add Service
                              </button>
                            </div>

                            {/* Add Product Button - Only show if Inventory app is installed and setting is enabled */}
                            {isInventoryAppInstalled && showInventoryInBookings && (
                              <div className="mt-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsSelectInventoryItemModalOpen(true);
                                  }}
                                  className="w-full px-4 py-4 border-2 border-dashed border-green-300 rounded-xl text-sm font-semibold text-green-600 hover:text-green-800 hover:border-green-400 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  Add Product
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* Add new vehicle button or Add Service button for Home Services */}
                    {(() => {
                      const isHomeServices = selectedBusiness?.industry === 'Home Services';
                      
                      if (selectedServices.length === 0) {
                        // No services yet - show appropriate button based on industry
                        if (isHomeServices) {
                          // For Home Services, show "Add Service" directly without requiring vehicle
                          return (
                            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setCurrentVehicle(null); // Set to null for Home Services
                                  setIsSelectServiceModalOpen(true);
                                }}
                                className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Add Service
                              </button>
                            </div>
                          );
                        } else {
                          // For Automotive, require vehicle selection first
                          return (
                            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsSelectVehicleModalOpen(true);
                                }}
                                className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Select Vehicle
                              </button>
                            </div>
                          );
                        }
                      } else {
                        // Has services - show "Add Another Vehicle" for Automotive, or "Add Service" for Home Services
                        if (isHomeServices) {
                          return (
                            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setCurrentVehicle(null); // Set to null for Home Services
                                  setIsSelectServiceModalOpen(true);
                                }}
                                className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Add Service
                              </button>
                            </div>
                          );
                        } else {
                          return (
                            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsSelectVehicleModalOpen(true);
                                }}
                                className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Add Another Vehicle
                              </button>
                            </div>
                          );
                        }
                      }
                    })()}
                  </div>
                  )}
                </div>
              )}




                {/* Scheduling Section */}
                {selectedServices.length > 0 && (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => !isUnscheduled && setSchedulePickerOpen(true)}
                        disabled={isUnscheduled}
                        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all text-left touch-manipulation ${
                          isUnscheduled
                            ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30 active:scale-[0.98]'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex-shrink-0">
                          <Clock className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Schedule</div>
                          <div className={`text-sm font-medium truncate ${scheduleData.startDate && scheduleData.startTime ? 'text-gray-900' : 'text-blue-600'}`}>
                            {isUnscheduled ? (
                              <span className="text-gray-500">No date — unscheduled</span>
                            ) : scheduleData.startDate && scheduleData.startTime ? (
                              <>
                                {scheduleData.dropOffDate && scheduleData.dropOffTime && (
                                  <span className="text-gray-500">
                                    Drop off: {(() => { try { return `${format(new Date(scheduleData.dropOffDate + 'T00:00:00'), 'MMM d')} ${format(new Date(`2000-01-01T${scheduleData.dropOffTime}`), 'h:mm a')}`; } catch { return ''; } })()} · 
                                  </span>
                                )}
                                {(() => { try { return `${format(new Date(scheduleData.startDate + 'T00:00:00'), 'EEE, MMM d')} ${format(new Date(`2000-01-01T${scheduleData.startTime}`), 'h:mm a')}`; } catch { return scheduleData.startDate; } })()}
                                {scheduleData.endDate && scheduleData.endTime && (
                                  <span className="text-gray-500">
                                    {' → '}
                                    {(() => { try { return `${format(new Date(scheduleData.endDate + 'T00:00:00'), 'MMM d')} ${format(new Date(`2000-01-01T${scheduleData.endTime}`), 'h:mm a')}`; } catch { return scheduleData.endDate; } })()}
                                  </span>
                                )}
                              </>
                            ) : 'Set schedule'}
                          </div>
                        </div>
                        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 -rotate-90" />
                      </button>

                      {schedulePickerOpen && selectedBusiness?.id && (
                        <SchedulePickerPanel
                          isOpen={schedulePickerOpen}
                          onClose={() => setSchedulePickerOpen(false)}
                          businessId={selectedBusiness.id}
                          showDropOff={true}
                          defaultView="month"
                          dropOffDate={scheduleData.dropOffDate}
                          dropOffTime={scheduleData.dropOffTime}
                          startDate={scheduleData.startDate}
                          startTime={scheduleData.startTime}
                          endDate={scheduleData.endDate}
                          endTime={scheduleData.endTime}
                          serviceDuration={totalDurationMinutes}
                          onConfirm={async (startDate, startTime, endDate, endTime, resourceId, dropOffDate, dropOffTime, resourceIsAsset) => {
                            setScheduleData(prev => ({
                              ...prev,
                              dropOffDate: dropOffDate || '',
                              dropOffTime: dropOffTime || '',
                              startDate,
                              startTime,
                              endDate,
                              endTime
                            }));
                            setIsServicesSectionExpanded(false);
                            // Asset column picked in the week grid → assign the asset
                            if (resourceId && resourceIsAsset) {
                              if (!selectedBookingAssets.some(a => a.id === resourceId)) {
                                try {
                                  const { data } = await supabase
                                    .from('assets')
                                    .select('id, name, image_url')
                                    .eq('id', resourceId)
                                    .single();
                                  if (data) setSelectedBookingAssets(prev => [...prev, data]);
                                } catch (err) {
                                  console.error('Failed to fetch asset for schedule selection:', err);
                                }
                              }
                              return;
                            }
                            if (resourceId && !selectedResources.some(r => r.id === resourceId)) {
                              try {
                                const { data } = await supabase
                                  .from('resources')
                                  .select('id, first_name, last_name, avatar_url, email')
                                  .eq('id', resourceId)
                                  .single();
                                if (data) {
                                  const newResources = [...selectedResources, data];
                                  setSelectedResources(newResources);
                                  saveBookingProgress({ selectedResources: newResources });
                                  return;
                                }
                              } catch (err) {
                                console.error('Failed to fetch resource for schedule selection:', err);
                              }
                            }
                            if (!resourceId && selectedResources.length === 0 && selectedBookingAssets.length === 0 && selectedCustomer) {
                              setIsSelectResourceModalOpen(true);
                            }
                          }}
                          disabled={loading}
                        />
                      )}

                      {/* Unscheduled toggle — create the booking with no date yet */}
                      <button
                        type="button"
                        onClick={() => {
                          setIsUnscheduled((prev) => {
                            const next = !prev;
                            if (next) {
                              setSchedulePickerOpen(false);
                              setScheduleData((sd) => ({
                                ...sd,
                                dropOffDate: '',
                                dropOffTime: '',
                                startDate: '',
                                startTime: '',
                                endDate: '',
                                endTime: '',
                              }));
                            }
                            return next;
                          });
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left active:scale-[0.98] touch-manipulation ${
                          isUnscheduled
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${isUnscheduled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                          <CalendarClock className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold">
                            {isUnscheduled ? 'Unscheduled — no date set' : 'Set as unscheduled'}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {isUnscheduled
                              ? 'This booking will be saved without a date.'
                              : "Don't have a date yet? Save it to the unscheduled list."}
                          </div>
                        </div>
                        <div className={`flex items-center justify-center w-5 h-5 rounded-full border-2 flex-shrink-0 ${isUnscheduled ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                          {isUnscheduled ? <Check className="h-3 w-3 text-white" /> : null}
                        </div>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Resource Selection — only after schedule is set */}
                {selectedServices.length > 0 && scheduleData.startDate && scheduleData.startTime && (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <button
                      type="button"
                      onClick={() => setIsResourcesExpanded(!isResourcesExpanded)}
                      className="w-full flex items-center justify-end mb-4"
                    >
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isResourcesExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Collapsed State - Show resource icons and add button */}
                    {!isResourcesExpanded && (
                      <div className="mb-2">
                        {(selectedResources.length > 0 || selectedBookingAssets.length > 0) && (
                          <div className="flex items-center space-x-2 mb-2">
                            {selectedResources.map((resource) => (
                              <div key={resource.id} className="relative group">
                                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-sm overflow-hidden">
                                  {resource.avatar_url ? (
                                    <img 
                                      src={resource.avatar_url} 
                                      alt={`${resource.first_name} ${resource.last_name}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-xs font-bold text-white">
                                      {getPersonInitials(resource.first_name, resource.last_name)}
                                    </span>
                                  )}
                                </div>
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                  {resource.first_name} {resource.last_name}
                                </div>
                              </div>
                            ))}
                            {/* Assets picked from the schedule grid — orange tiles */}
                            {selectedBookingAssets.map((asset) => (
                              <div key={asset.id} className="relative group">
                                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-sm overflow-hidden">
                                  {asset.image_url ? (
                                    <img src={asset.image_url} alt={asset.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-xs font-bold text-white">
                                      {(asset.name || 'A').trim().slice(0, 2).toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                  {asset.name} (asset)
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Add Resource Button in Collapsed State */}
                        <button
                          type="button"
                          onClick={() => setIsSelectResourceModalOpen(true)}
                          className="w-full px-2 py-1.5 border-2 border-dashed border-blue-300 rounded-lg text-xs font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                          style={{ minHeight: '32px' }}
                        >
                          {selectedResources.length === 0 ? 'Add Resources' : 'Add Another Resource'}
                        </button>
                      </div>
                    )}
                    
                    {/* Expanded State - Show full resource details */}
                    {isResourcesExpanded && (
                      <>
                        {selectedResources.length > 0 ? (
                      <div className="space-y-3">
                        {selectedResources.map((resource) => (
                          <div key={resource.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <div className="flex items-center space-x-3">
                                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden">
                                {resource.avatar_url ? (
                                  <img 
                                    src={resource.avatar_url} 
                                    alt={`${resource.first_name} ${resource.last_name}`}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-sm font-medium text-white">
                                    {getPersonInitials(resource.first_name, resource.last_name)}
                                  </span>
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {resource.first_name} {resource.last_name}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {resource.email}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleResourceRemove(resource.id)}
                              className="text-sm font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        {/* Assets picked from the schedule grid */}
                        {selectedBookingAssets.map((asset) => (
                          <div key={asset.id} className="flex items-center justify-between bg-orange-50/60 rounded-lg p-3 border border-orange-200/60">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center overflow-hidden">
                                {asset.image_url ? (
                                  <img src={asset.image_url} alt={asset.name} className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-sm font-medium text-white">
                                    {(asset.name || 'A').trim().slice(0, 2).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900">{asset.name}</div>
                                <div className="text-sm text-gray-600">Asset</div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelectedBookingAssets(prev => prev.filter(a => a.id !== asset.id))}
                              className="text-sm font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setIsSelectResourceModalOpen(true)}
                          className="w-full mt-2 px-3 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          Add Another Resource
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsSelectResourceModalOpen(true)}
                        className="w-full px-3 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        style={{ minHeight: '48px' }}
                      >
                        Add Resources
                      </button>
                    )}
                      </>
                    )}
                  </div>
                )}

                {/* Color Picker — only after schedule is set */}
                {selectedServices.length > 0 && scheduleData.startDate && scheduleData.startTime && (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Booking Colour</h3>
                    <div className="grid grid-cols-5 gap-2">
                      {/* Current Color */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedColorMethod('custom');
                          setIsColorPickerModalOpen(true);
                        }}
                        className={`flex flex-col items-center justify-center p-2 border-2 rounded-lg transition-colors ${
                          selectedColorMethod === 'custom' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300 opacity-60 hover:opacity-80'
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full mb-1 overflow-hidden">
                          {bookingSecondaryColor ? (
                            <div className="w-full h-full flex">
                              <div 
                                className="w-1/2 h-full"
                                style={{ backgroundColor: bookingColor }}
                              />
                              <div 
                                className="w-1/2 h-full"
                                style={{ backgroundColor: bookingSecondaryColor }}
                              />
                            </div>
                          ) : (
                            <div 
                              className="w-full h-full"
                              style={{ backgroundColor: bookingColor }}
                            />
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-900">Current</span>
                      </button>

                      {/* Color Presets */}
                      {colorPresets.map((preset) => (
                        <div key={preset.id} className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedColorMethod(preset.id);
                              setBookingColor(preset.primary_color);
                              setBookingSecondaryColor(preset.secondary_color || null);
                              saveBookingProgress({ 
                                bookingColor: preset.primary_color, 
                                bookingSecondaryColor: preset.secondary_color 
                              });
                            }}
                            className={`flex flex-col items-center justify-center p-2 border-2 rounded-lg transition-colors w-full ${
                              selectedColorMethod === preset.id 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-200 hover:border-gray-300 opacity-60 hover:opacity-80'
                            }`}
                          >
                            <div className="w-8 h-8 rounded-full mb-1 overflow-hidden">
                              {preset.secondary_color ? (
                                <div className="w-full h-full flex">
                                  <div 
                                    className="w-1/2 h-full"
                                    style={{ backgroundColor: preset.primary_color }}
                                  />
                                  <div 
                                    className="w-1/2 h-full"
                                    style={{ backgroundColor: preset.secondary_color }}
                                  />
                                </div>
                              ) : (
                                <div 
                                  className="w-full h-full"
                                  style={{ backgroundColor: preset.primary_color }}
                                />
                              )}
                            </div>
                            <span className="text-xs font-medium text-gray-900">{preset.title}</span>
                          </button>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              
                              const confirmed = window.confirm(
                                `Are you sure you want to delete the "${preset.title}" color preset? This action cannot be undone.`
                              );
                              
                              if (!confirmed) return;
                              
                              try {
                                await deleteColorPreset(preset.id);
                                setColorPresets(prev => prev.filter(p => p.id !== preset.id));
                                if (selectedColorMethod === preset.id) {
                                  setSelectedColorMethod('custom');
                                }
                              } catch (error) {
                                console.error('Error deleting color preset:', error);
                              }
                            }}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-medium shadow-sm transition-colors"
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      {/* Add Preset Button */}
                      <button
                        type="button"
                        onClick={() => setIsCreateColorPresetModalOpen(true)}
                        className="flex flex-col items-center justify-center p-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors opacity-60 hover:opacity-80"
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mb-1">
                          <Plus className="w-4 h-4 text-gray-500" />
                        </div>
                        <span className="text-xs font-medium text-gray-600">Add</span>
                      </button>
                    </div>
                  </div>
                )}

              </div>
          </form>

          {/* Fixed bottom — payment summary + submit button */}
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]">
            {selectedServices.length > 0 && (
              <div className="mb-2">
                {(() => {
                  // Per-service discount (carried from a proposal) reduces that service's line.
                  const lineDisc = (s: any): number => {
                    const base = s?.priceOption?.price || 0;
                    const dv = s?.discount_value || 0;
                    if (!dv || base <= 0) return 0;
                    const d = s?.discount_type === 'percentage' ? (base * dv) / 100 : dv;
                    return Math.min(Math.max(Math.round(d * 100) / 100, 0), base);
                  };
                  const lineDiscountTotal = selectedServices.reduce((sum, s) => sum + lineDisc(s), 0);

                  const subtotal = selectedServices.reduce((sum, s) => {
                    let t = s.priceOption.price;
                    const addons = selectedServiceAddons[s.service.id] || [];
                    t += addons.reduce((a, addon) => a + addon.price, 0);
                    return sum + t;
                  }, 0) + selectedProducts.reduce((sum, p) => sum + (p.product.price * p.product.quantity), 0);

                  // Per-service tax base is net of that service's own discount.
                  const svcSubs = selectedServices.map(s => {
                    let total = Math.max(0, s.priceOption.price - lineDisc(s));
                    const addons = selectedServiceAddons[s.service.id] || [];
                    total += addons.reduce((a, addon) => a + addon.price, 0);
                    return { serviceId: s.service.id, total, taxExempt: s.service.tax_exempt };
                  });
                  // Booking-level discount stacks on top of the per-service discounts.
                  const baseForBookingDisc = Math.max(0, subtotal - lineDiscountTotal);
                  const bookingDiscountAmt = pendingDiscount
                    ? pendingDiscount.type === 'percentage'
                      ? Math.round((baseForBookingDisc * (pendingDiscount.amount / 100)) * 100) / 100
                      : Math.min(pendingDiscount.amount, baseForBookingDisc)
                    : 0;
                  const discountAmt = Math.round((lineDiscountTotal + bookingDiscountAmt) * 100) / 100;
                  const gstBase = Math.max(0, Math.round((subtotal - discountAmt) * 100) / 100);
                  const lineMultiplier = subtotal > 0 ? gstBase / subtotal : 0;

                  const taxLines: { label: string; amount: number }[] = [];
                  for (const rate of businessTaxRates) {
                    taxLines.push({
                      label: `${rate.name} (${rate.rate}%)`,
                      amount: Math.round((gstBase * rate.rate) / 100 * 100) / 100,
                    });
                  }
                  const additionalTaxMap = new Map<string, { name: string; rate: number; amount: number }>();
                  for (const atx of serviceAdditionalTaxes) {
                    const svc = svcSubs.find(s => s.serviceId === atx.service_id);
                    if (!svc) continue;
                    const key = `${atx.name}_${atx.rate}`;
                    const scaledLine = svc.total * lineMultiplier;
                    if (additionalTaxMap.has(key)) additionalTaxMap.get(key)!.amount += scaledLine * atx.rate / 100;
                    else additionalTaxMap.set(key, { name: atx.name, rate: atx.rate, amount: scaledLine * atx.rate / 100 });
                  }
                  for (const atx of additionalTaxMap.values()) {
                    taxLines.push({ label: `${atx.name} (${atx.rate}%)`, amount: atx.amount });
                  }
                  const totalTax = taxLines.reduce((s, l) => s + l.amount, 0);
                  const grandTotal = subtotal - discountAmt + totalTax;

                  return (
                    <div className="space-y-1">
                      {/* Resource & Schedule summary */}
                      {(selectedResources.length > 0 || (scheduleData.startDate && scheduleData.startTime)) && (
                        <div className="flex items-center gap-2 text-[11px] text-gray-500 flex-wrap mb-0.5">
                          {selectedResources.length > 0 && (
                            <span className="flex items-center gap-1">
                              <span className="inline-block w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                              <span className="font-medium text-gray-700">{selectedResources.map(r => `${r.first_name} ${r.last_name}`).join(', ')}</span>
                            </span>
                          )}
                          {selectedResources.length > 0 && scheduleData.startDate && scheduleData.startTime && (
                            <span className="text-gray-300">·</span>
                          )}
                          {scheduleData.startDate && scheduleData.startTime && (
                            <span className="font-medium text-gray-700">
                              {(() => { try { return format(new Date(`${scheduleData.startDate}T${scheduleData.startTime}`), 'EEE, MMM d — h:mm a'); } catch { return ''; } })()}
                              {scheduleData.endDate && scheduleData.endTime && (
                                <span className="text-gray-400 font-normal"> → </span>
                              )}
                              {scheduleData.endDate && scheduleData.endTime && (
                                scheduleData.endDate !== scheduleData.startDate
                                  ? (() => { try { return format(new Date(`${scheduleData.endDate}T${scheduleData.endTime}`), 'EEE, MMM d — h:mm a'); } catch { return ''; } })()
                                  : (() => { try { return format(new Date(`${scheduleData.endDate}T${scheduleData.endTime}`), 'h:mm a'); } catch { return ''; } })()
                              )}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Subtotal + taxes + discount + total */}
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-3 text-gray-500 flex-wrap">
                          <span>Subtotal <span className="font-medium text-gray-700">${subtotal.toFixed(2)}</span></span>
                          {discountAmt > 0 && (
                            <span className="text-green-600">
                              Discount <span className="font-medium">-${discountAmt.toFixed(2)}</span>
                            </span>
                          )}
                          {taxLines.map((line, i) => (
                            <span key={i}>{line.label} <span className="font-medium text-gray-700">${line.amount.toFixed(2)}</span></span>
                          ))}
                        </div>
                        <div className="text-sm font-bold text-gray-900 flex-shrink-0">
                          Total ${grandTotal.toFixed(2)}
                        </div>
                      </div>
                      {/* Deposit info */}
                      {(sourceProposal || initialProposal) && proposalDepositPaid > 0 && (
                        <div className="flex items-center justify-between text-xs pt-0.5">
                          <span className="text-green-600 font-medium">Deposit Paid: ${proposalDepositPaid.toFixed(2)}</span>
                          <span className="text-gray-700 font-semibold">Balance: ${(grandTotal - proposalDepositPaid).toFixed(2)}</span>
                        </div>
                      )}
                      {/* Add discount button */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setIsDiscountModalOpen(true)}
                          className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          {pendingDiscount ? 'Edit Discount' : '+ Add Discount'}
                        </button>
                        {pendingDiscount && (
                          <button
                            type="button"
                            onClick={() => setPendingDiscount(null)}
                            className="text-[11px] font-semibold text-red-500 hover:text-red-700 transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            {(() => {
              const noTitle = !title;
              const noCustomer = !selectedCustomer;
              const noServices = selectedServices.length === 0;
              const noSchedule = !isUnscheduled && (!scheduleData.startDate || !scheduleData.startTime);
              const noResources = !isUnscheduled && selectedResources.length === 0 && selectedBookingAssets.length === 0;
              const noSource = !selectedCustomerSource && !isWarrantyMode;
              const durationTooShort = selectedServices.length > 0 && totalDurationMinutes < 30;
              const isDisabled = loading || noTitle || noCustomer || noServices || noSchedule || noResources || noSource || durationTooShort;

              return (
                <>
                  {durationTooShort && (
                    <div className="mb-2 flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-xl">
                      <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                      <span className="text-xs text-red-700 font-medium">
                        Min 30 minutes required ({totalDurationMinutes} min currently)
                      </span>
                    </div>
                  )}
                  {/* Recurring series — hidden for unscheduled (no date to repeat from) and warranties */}
                  {!isUnscheduled && !isWarrantyMode && (
                    <div className="mb-2">
                      <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={isRecurring}
                          onChange={(e) => setIsRecurring(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30"
                        />
                        <span className="text-[13px] text-gray-600">Recurring booking</span>
                      </label>
                      {isRecurring && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Repeats</label>
                            <select
                              value={recurringFrequency}
                              onChange={(e) => setRecurringFrequency(e.target.value as SeriesFrequency)}
                              className="w-full px-2.5 py-1.5 text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                            >
                              {SERIES_FREQUENCIES.map(f => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">For</label>
                            <select
                              value={recurringLengthMonths}
                              onChange={(e) => setRecurringLengthMonths(Number(e.target.value))}
                              className="w-full px-2.5 py-1.5 text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                            >
                              {SERIES_LENGTHS.map(l => (
                                <option key={l.value} value={l.value}>{l.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <label className="mb-2 flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={removeFromAutomations}
                      onChange={(e) => setRemoveFromAutomations(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30"
                    />
                    <span className="text-[13px] text-gray-600">Remove from automations</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isDisabled}
                    className={`w-full inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:cursor-not-allowed disabled:transform-none ${
                      isDisabled
                        ? 'bg-gray-300'
                        : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Creating Booking...
                      </>
                    ) : (
                      'Create Booking'
                    )}
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={handleCustomerSelect}
      />

      <ExistingCustomerProposalsModal
        isOpen={isExistingProposalsModalOpen}
        proposals={existingProposals}
        customerName={
          selectedCustomer
            ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim()
            : undefined
        }
        onIgnore={handleIgnoreExistingProposals}
        onConvert={handleConvertExistingProposal}
        converting={convertingExistingProposal}
      />

      <SelectCustomerModal
        isOpen={isSelectReferralSourceModalOpen}
        onClose={() => setIsSelectReferralSourceModalOpen(false)}
        onSelect={async (customer) => {
          setReferralSourceCustomer(customer);
          setIsSelectReferralSourceModalOpen(false);
        }}
      />

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={handleSelectVehicle}
        vehicles={selectedCustomer?.vehicles || []}
        selectedVehicles={selectedServices.map(s => s.vehicle).filter((v): v is CustomerVehicle => v !== null)}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => setIsCreateVehicleModalOpen(false)}
        onSuccess={handleVehicleCreated}
        customerId={selectedCustomer?.id || ''}
      />

      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => setIsEditCustomerModalOpen(false)}
        onSuccess={(updatedCustomer) => {
          setIsEditCustomerModalOpen(false);
          setSelectedCustomer(updatedCustomer);
        }}
        customer={selectedCustomer}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={async (service, priceOption) => {
          // Check industry - for Home Services, vehicle is optional
          const isHomeServices = selectedBusiness?.industry === 'Home Services';
          
          if (!isHomeServices && !currentVehicle) {
            toast({
              title: 'No vehicle selected',
              description: 'Please select a vehicle first',
              variant: 'destructive'
            });
            return;
          }
          
          handleAddService(service, priceOption);
          return Promise.resolve();
        }}
        vehicleId={currentVehicle?.id}
      />

      <SelectInventoryItemModal
        isOpen={isSelectInventoryItemModalOpen}
        onClose={() => setIsSelectInventoryItemModalOpen(false)}
        onAdd={async (product) => {
          handleAddProduct(product);
          return Promise.resolve();
        }}
        onUpdateQuantity={async (product, newQuantity) => {
          handleUpdateProductQuantity(product, newQuantity);
          return Promise.resolve();
        }}
        currentBookingProducts={selectedProducts}
      />

      <SelectResourceModal
        isOpen={isSelectResourceModalOpen}
        onClose={() => setIsSelectResourceModalOpen(false)}
        onConfirm={({ resources }) => {
          setSelectedResources(resources);
          saveBookingProgress({ selectedResources: resources });
        }}
        selectedResources={selectedResources}
        confirmLabel="Add to booking"
      />

      <EditVehicleModal
        isOpen={isEditVehicleModalOpen}
        onClose={() => {
          setIsEditVehicleModalOpen(false);
          setSelectedVehicleToEdit(null);
        }}
        onUpdate={(updatedVehicle: CustomerVehicle) => {
          // Update the vehicle in the selectedServices array
          setSelectedServices(prev => prev.map(s => 
            s.vehicle.id === updatedVehicle.id
              ? { ...s, vehicle: updatedVehicle }
              : s
          ));
          setIsEditVehicleModalOpen(false);
          setSelectedVehicleToEdit(null);
        }}
        vehicle={selectedVehicleToEdit}
      />

      <EditServiceModal
        isOpen={isEditServiceModalOpen}
        onClose={() => {
          setIsEditServiceModalOpen(false);
          setSelectedServiceToEdit(null);
        }}
        onSave={async (updatedService) => {
          // Update the service in the selectedServices array
          setSelectedServices(prev => prev.map(s => 
            s === selectedServiceToEdit
              ? {
                  ...s,
                  service: {
                    ...s.service,
                    name: updatedService.name,
                    description: updatedService.description
                  },
                  priceOption: {
                    ...s.priceOption,
                    price: updatedService.price,
                    duration_minutes: updatedService.duration_minutes
                  }
                }
              : s
          ));
          setIsEditServiceModalOpen(false);
          setSelectedServiceToEdit(null);
        }}
        service={selectedServiceToEdit ? {
          name: selectedServiceToEdit.service.name,
          description: selectedServiceToEdit.service.description,
          price: selectedServiceToEdit.priceOption.price,
          duration_minutes: selectedServiceToEdit.priceOption.duration_minutes,
          is_active: true,
          tax_rate: selectedServiceToEdit.service.tax_rate || 5,
          tax_exempt: selectedServiceToEdit.service.tax_exempt || false,
          addons: selectedServiceAddons[selectedServiceToEdit.service.id] || []
        } : null}
      />

      <SelectServiceAddonsModal
        isOpen={isSelectAddonsModalOpen}
        onClose={() => {
          setIsSelectAddonsModalOpen(false);
          setCurrentService(null);
        }}
        serviceId={currentService?.id || ''}
        onSelect={handleAddAddons}
        selectedAddons={currentService ? selectedServiceAddons[currentService.id] || [] : []}
      />

      <ColorPickerModal
        isOpen={isColorPickerModalOpen}
        onClose={() => setIsColorPickerModalOpen(false)}
        onColorSelect={(color, secondaryColor) => {
          setSelectedColorMethod('custom');
          setBookingColor(color);
          setBookingSecondaryColor(secondaryColor || null);
          saveBookingProgress({ bookingColor: color, bookingSecondaryColor: secondaryColor });
          setIsColorPickerModalOpen(false);
        }}
        currentColor={bookingColor}
        currentSecondaryColor={bookingSecondaryColor || undefined}
      />

      <CreateColorPresetModal
        isOpen={isCreateColorPresetModalOpen}
        onClose={() => setIsCreateColorPresetModalOpen(false)}
        onSave={async (title, primaryColor, secondaryColor) => {
          if (!selectedBusiness?.id) return;
          
          try {
            const newPreset = await createColorPreset({
              business_id: selectedBusiness.id,
              title,
              primary_color: primaryColor,
              secondary_color: secondaryColor
            });
            setColorPresets(prev => [...prev, newPreset]);
            setIsCreateColorPresetModalOpen(false);
          } catch (error) {
            console.error('Error creating color preset:', error);
          }
        }}
      />

      {/* Inline Discount Modal */}
      {isDiscountModalOpen && createPortal(
        <div className="fixed inset-0 z-[100200] flex items-end sm:items-center justify-center" onClick={() => setIsDiscountModalOpen(false)}>
          <div className="fixed inset-0 bg-black/40" />
          <div
            className="relative bg-white w-full sm:w-[400px] sm:rounded-2xl rounded-t-2xl p-5 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Add Discount</h3>
              <button type="button" onClick={() => setIsDiscountModalOpen(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <InlineDiscountForm
              initialDiscount={pendingDiscount}
              bookingSubtotal={selectedServices.reduce((sum, s) => {
                let t = s.priceOption.price;
                const addons = selectedServiceAddons[s.service.id] || [];
                t += addons.reduce((a, addon) => a + addon.price, 0);
                return sum + t;
              }, 0) + selectedProducts.reduce((sum, p) => sum + (p.product.price * p.product.quantity), 0)}
              onApply={(discount) => { setPendingDiscount(discount); setIsDiscountModalOpen(false); }}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Field Quote Link Picker — shown after "Create Booking", before saving */}
      {fqPickerOpen && createPortal(
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 100240 }}
          onClick={() => { setFqPickerOpen(false); setLoading(false); }}
        >
          <div className="fixed inset-0 bg-black/50" style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }} />
          <div
            className="relative bg-white w-full sm:w-[440px] sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Link a field quote?</h3>
              <p className="text-sm text-gray-500 mt-1">
                This customer has field quotes that may have led to this booking. Select any to link, or skip.
              </p>
            </div>

            <div className="px-5 py-3 overflow-y-auto space-y-2">
              {fqCandidates.map(fq => {
                const checked = fqSelectedIds.includes(fq.id);
                const matchLabel =
                  fq.matched_by === 'proposal' ? 'From proposal'
                  : fq.matched_by === 'opportunity' ? 'Same opportunity'
                  : fq.matched_by === 'project' ? 'Same project'
                  : 'Same customer';
                const dateLabel = fq.start_date
                  ? format(new Date(fq.start_date), 'MMM d, yyyy')
                  : 'No date set';
                return (
                  <button
                    type="button"
                    key={fq.id}
                    onClick={() => setFqSelectedIds(prev =>
                      prev.includes(fq.id) ? prev.filter(id => id !== fq.id) : [...prev, fq.id]
                    )}
                    className={`w-full text-left flex items-start gap-3 rounded-[12px] border px-3 py-3 transition-colors ${
                      checked ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ${
                      checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white'
                    }`}>
                      {checked && <span className="text-[11px] leading-none">✓</span>}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {fq.project_title || 'Field quote'}
                        </span>
                        <span className="text-[11px] font-medium text-blue-600 bg-blue-50 rounded-full px-2 py-0.5 flex-shrink-0">
                          {matchLabel}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500 truncate">
                        {dateLabel}
                        {fq.field_quote_address ? ` · ${fq.field_quote_address}` : ''}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex gap-3" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}>
              <button
                type="button"
                onClick={() => {
                  resolvedFieldQuoteIdsRef.current = [];
                  setFqPickerOpen(false);
                  void handleSubmit();
                }}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={() => {
                  resolvedFieldQuoteIdsRef.current = [...fqSelectedIds];
                  setFqPickerOpen(false);
                  void handleSubmit();
                }}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-colors disabled:opacity-50"
              >
                {fqSelectedIds.length > 0 ? `Link ${fqSelectedIds.length} & create` : 'Create without link'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create Customer Source Modal */}
      {isCreateSourceModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 100220, paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}
          onClick={(e) => {
            e.stopPropagation();
            setIsCreateSourceModalOpen(false);
            setNewSourceName('');
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Add Customer Source
              </h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Name
              </label>
              <input
                type="text"
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter source name (e.g., Google Ads, Referral)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSourceName.trim()) {
                    handleCreateCustomerSource();
                  }
                }}
                autoFocus
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={createSourceLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateCustomerSource();
                }}
                disabled={!newSourceName.trim() || createSourceLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createSourceLoading ? 'Creating...' : 'Create Source'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simple Workflow Modal - Only render if workflows are enabled */}
      {workflowsEnabled && (
        <SimpleWorkflowModal
          isOpen={isSimpleWorkflowModalOpen}
        onClose={() => {
          setIsSimpleWorkflowModalOpen(false);
          setCurrentServiceForWorkflow(null);
        }}
        onSkip={handleWorkflowSkip}
        onContinue={handleWorkflowContinue}
        serviceName={currentServiceForWorkflow?.name || ''}
        serviceId={currentServiceForWorkflow?.id || ''}
        isEditing={(() => {
          // Check if this service instance already has workflow data stored
          if (!currentServiceForWorkflow?.instanceId) return false;
          const existingService = selectedServices.find(s => s.instanceId === currentServiceForWorkflow.instanceId);
          return !!(existingService?.service.workflowData && existingService.service.workflowData.length > 0);
        })()}
        existingWorkflowData={(() => {
          // Get existing workflow data for this service instance
          if (!currentServiceForWorkflow?.instanceId) return [];
          const existingService = selectedServices.find(s => s.instanceId === currentServiceForWorkflow.instanceId);
          if (existingService?.service.workflowData) {
            // Convert from stored format to BookingWorkflowStep format
            return existingService.service.workflowData.map((step: any) => ({
              workflow_step_library_id: step.workflow_step_library_id,
              title: step.title,
              description: step.description || '',
              order_index: step.order_index,
              photos_enabled: step.photos_enabled || false,
              assigned_resources: step.assigned_resources.map((resource: any) => ({
                id: resource.id,
                first_name: resource.name.split(' ')[0] || '',
                last_name: resource.name.split(' ').slice(1).join(' ') || '',
                email: resource.email || '',
                avatar_url: resource.avatar_url
              }))
            }));
          }
          return [];
        })()}
      />
      )}
    </div>,
    document.body
  );
}